/**
 * usePrototyperWorkbenchFacade
 *
 * Core streaming hook that connects AI SDK useChat to the @ideoz/workbench
 * stores. Manages WebContainer lifecycle, tool call dispatch, annotation
 * handling (brandLogo, projectCreated), and credits.
 *
 * Uses AI SDK 6+ (`ai` + `@ai-sdk/react`): `sendMessage`, `DefaultChatTransport`,
 * deferred `addToolOutput` (never sync inside `onToolCall`), `onData`, and `status`.
 */

import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { refreshAccessToken } from '@ideoz/core';
import { useStore } from '@nanostores/react';
import type { WebContainer } from '@webcontainer/api';
import {
  bootWebContainer,
  getWebContainer,
  workbenchStore,
  executeWriteFile,
  persistUserFile,
  persistAssetFile,
  executeBuild,
  BuildAbortedError,
  executeConfigureSession,
  executeSearchReplace,
  executePrune,
  executeDeleteFile,
  strippedUnifiedDiff,
  hashFileContent,
  flushDirtyEditorFilesBeforeStream,
  recordProductionBuildOutcome,
  resetPersistedBuildCache,
  markSnapshotPreviewReady,
  restartDevServerAfterSnapshot,
  recoverPreviewEnvironment,
  syncWorkbenchStoreWithWebContainerPersist,
  buildPrototypeSnapshotZipBlob,
  prototypeSnapshotContentFingerprint,
  waitForTemplateReady,
  applySnapshotZipToWebContainer,
  applySnapshotZipToStore,
  resetPreviewStoreToTemplate,
  readCachedSnapshotZip,
  buildSnapshotZipFromFiles,
  WORKBENCH_SAVE_CONFLICT_KEEP_MINE_EVENT,
  WORKBENCH_SAVE_CONFLICT_LOAD_LATEST_EVENT,
  snapshotZipCacheKey,
  writeCachedSnapshotZip,
  wcPerfAsync,
  HOT_EDIT_BRIDGE_REL_PATHS,
  getMergedFileContent,
  resetContainerToTemplate,
  detectWorkbenchCapability,
  previewCrashRepair,
  takePendingAsk,
  type PreviewCrashRepair,
  type WorkbenchPreviewCrash,
  type WorkbenchTabOwnership,
} from '@ideoz/workbench';
import { ApiUrls, prototyperService } from '@ideoz/core';
import { useAccessToken } from '@ideoz/core/hooks/useAccessToken';
import {
  isBenignMissingSnapshotError,
  isSupersededRestoreError,
} from './utils/snapshotRestoreErrors';
import {
  type AutoSnapshotReason,
  recordNewestSnapshotFingerprint,
  snapshotDedupKey,
  shouldDeferForBusy,
  shouldSkipForCooldown,
} from './utils/buildPhaseSnapshotGating';
import type { ResumeBuildOutcome } from './utils/resumeBuildGating';
import {
  lastToolStepBuildFailed,
  shouldAutoContinueAfterFailedBuild,
} from './utils/lastAssistantClientBuildFailedInUiMessages';
import { lastAssistantHasPendingClientTools } from './utils/lastAssistantHasPendingClientTools';
import { lastToolStepBuildPassed, stoppedTurnOutcome } from './utils/stoppedTurn';
import { saveEditsToBuilder, WORKBENCH_BUILDER_SAVE_RETRY_EVENT } from './utils/builderSave';
import { isServerExecutedPrototyperTool } from './utils/serverExecutedPrototyperTools';
import { missingServerBuiltFiles } from './utils/serverBuiltFiles';
import { scheduleStreamContinuation } from './utils/streamContinuation';
import {
  LIGHT_PREVIEW_REQUESTED,
  lightPreviewOwnsPreview,
  useLightPreviewRuntime,
} from './useLightPreviewRuntime';
import {
  MAX_BUILD_STREAM_TRANSPORT_RETRIES,
  backoffDelayMs,
  classifyBuildStreamError,
  createBuildStreamFetch,
  parseBuildStreamErrorData,
  retryPolicyFor,
  userMessageFor,
  type BuildStreamError,
} from './utils/buildStreamErrors';
import { trimTranscriptForRequest } from './utils/trimTranscriptForRequest';
import {
  createVersionTracker,
  getWorkbenchTabId,
  lastBuildStepOutput,
  type VersionTracker,
} from './utils/versionTracker';
import type { TrackedVersionFailure } from './utils/versionTracker';
import {
  NO_CREDITS_FOR_FIX_NOTICE,
  attentionFromPrototypes,
  decideAfterVersionFailure,
  fixProgressLabel,
  type AfterFailureAction,
  type VersionAttention,
} from './utils/autoFix';
import {
  indexTurnVersions,
  newerVerifiedVersion,
  pickBootstrapEntry,
  restorableEntries,
  type TurnVersion,
  type TurnVersionsLookup,
  type TurnVersionsStatus,
} from './utils/turnVersions';
import {
  TURN_BUDGET_CHECK_MS,
  TURN_BUDGET_MESSAGE,
  TURN_BUDGET_SIGNATURE,
  createTurnBudgets,
} from './utils/turnBudget';
import { browserVersionEventChannel, createVersionEvents, type VersionEvents } from './utils/versionEvents';
import {
  SnapshotObjectMissingError,
  isSnapshotObjectMissingError,
} from './utils/snapshotRestoreErrors';
import {
  allowOpportunisticBuild,
  computeMaxToolContinuations,
  isEditTurnUnfinished,
  pathsTouchedThisTurn,
  plannedPathsFromPlanComplete,
} from './utils/editLoopCompleteness';
import {
  bumpTurnGeneration,
  createRestoreMutex,
  createTurnSession,
  isTurnStale,
  withRestoreMutex,
  abortRestore,
  type TurnSession,
  type RestoreMutex,
} from './utils/workbenchTurnSession';
import type { ToolStep } from './components/ToolCallStatusList';
import type { BuildTimelineV2 } from './utils/prototyperBuildPhases';
import {
  applyExploreEventToTimeline,
  applyPlanCompleteToTimeline,
  createEmptyTimeline,
  markStreamEndedWithoutSuccessfulBuild,
  markStreamError,
  markStreamSuccess,
  mergeLiveTerminalSegments,
  mergeTimelinePreservingAnnotations,
  timelineFromToolSteps,
} from './utils/prototyperBuildPhases';

/** Gate ConversationPageTemplate auto build-stream until S3 snapshot restore is decided. */
export type WorkbenchSnapshotBootstrap = 'pending' | 'restored' | 'none' | 'failed';

export interface UsePrototyperWorkbenchFacadeOptions {
  /**
   * When true, bootstrap WebContainer as soon as the conversation has a Nest project scope
   * (snapshot restore needs a ready container regardless of prototype panel visibility).
   */
  needsImmediateWorkbenchBoot?: boolean;
  /**
   * When true, prototype panel is open — allows deferred boot for tmp chats (no Nest project yet).
   */
  streamingWorkbenchPrototypePanelVisible?: boolean;
  conversationId?: string;
  /**
   * Ideoz project UUID. Prototype ZIP snapshots are Nest project-scoped; without this,
   * auto-upload is skipped (tmp chat persists edits only in WebContainer until a project exists).
   */
  projectId?: string;
  tmpConversationId?: string;
  modelProvider: string;
  modelChoice: string;
  onCreditsRefresh?: () => void;
  /**
   * Chef `projectCreated` annotation — `projectId` is the **Chef** id (build-stream body only).
   * Do not treat it as the Ideoz project UUID for `/projects/:id/...` APIs.
   */
  onProjectCreated?: (chefProjectId: string) => void;
  /** SSE `demoLinkCreated` — persist `prototyper_demo_link` id for legacy Publish UI */
  onStreamDemoLinkId?: (id: number) => void;
  /**
   * Flask `stream_prototype_build` uses this id to wait for/select the persisted prototyper interaction.
   * Set immediately before triggering `startGeneration` (see ConversationPageTemplate → ConversationPage).
   */
  streamingBuildLlmInteractionIdRef?: MutableRefObject<string | undefined>;
  /** Set when a build-stream starts so sessionStorage / chat id are scoped per turn. */
  streamingLlmInteractionId?: string;
  /** Short label for prototyper_build_sessions (prior user message). */
  getPrototypeDisplayName?: () => string | undefined;
  /** Patch Redux assistant row (e.g. demo link id, success banner) by backend llm interaction id. */
  onPatchMessageByLlmInteractionId?: (llmInteractionId: string, patch: Record<string, unknown>) => void;
  /** Called when a build-stream turn completes with a successful production build. */
  onBuildStreamTurnCompleted?: (llmInteractionId: string) => void;
  /** Called after boot-time S3 snapshot restore finishes. */
  onSnapshotRestored?: (meta: {
    llmInteractionId?: string | null;
    buildVerified?: boolean;
  }) => void;
  /**
   * Whether the founder has prototype credits for an automatic fix attempt (P6.4b), read when
   * a fix would start. Without it the facade assumes credits.
   */
  hasPrototypeCredits?: () => boolean;
  /**
   * Start the build-stream for `llmInteractionId` as an automatic fix (P6.4b). The facade has
   * already marked that turn's next stream `turnKind: fix`; the host starts it like any build.
   */
  onAutoFixReady?: (llmInteractionId: string) => void;
  /**
   * When set (from `?restoreInteraction=` on a library-list click), boot-time
   * snapshot restore prefers this interaction over the conversation's newest
   * verified snapshot — required once a conversation owns more than one prototype.
   */
  preferredRestoreInteractionId?: string | null;
}

export type PrototypeSnapshotRestoreHint = {
  buildStatus?: string;
  hasBuildTimeline?: boolean;
  /**
   * Restore even when this turn is already the one on screen: after Stop the workspace holds the
   * stopped turn's files while the version on screen is unchanged.
   */
  force?: boolean;
};

const WEBCONTAINER_BOOT_WAIT_MS = 45_000;

async function waitForWebContainerReady(
  getWc: () => WebContainer | null | undefined,
  timeoutMs = WEBCONTAINER_BOOT_WAIT_MS,
): Promise<WebContainer | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const status = workbenchStore.bootStatus.get();
    if (status === 'error') {
      return null;
    }
    const wc = getWc();
    if (wc && status === 'ready') {
      return wc;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 150));
  }
  return getWc() ?? null;
}

function getToolInput(toolCall: { input?: unknown }): Record<string, unknown> {
  const input = toolCall.input;
  return input && typeof input === 'object' && input !== null && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : {};
}

const WORKBENCH_MANUAL_SKIP_FRAGMENTS = ['/node_modules/', '/.git/'] as const;
const WORKBENCH_MANUAL_MAX_TOTAL_CHARS = 380_000;
const WORKBENCH_MANUAL_MAX_FILE_CHARS = 60_000;
const WORKBENCH_MANUAL_MAX_FILES = 40;

const WORKBENCH_MANUAL_DIFF_MAX_FILE_CHARS = 12_800;
const WORKBENCH_MANUAL_DIFF_MAX_TOTAL_CHARS = 100_000;

function workbenchManualPathDisallowed(filePath: string): boolean {
  const p = filePath.replace(/\\/g, '/').replace(/^\/+/, '');
  return WORKBENCH_MANUAL_SKIP_FRAGMENTS.some((frag) => p.includes(frag));
}

function isHotEditBridgePath(filePath: string): boolean {
  return (HOT_EDIT_BRIDGE_REL_PATHS as readonly string[]).includes(filePath);
}

/**
 * The founder's edited files (code editor or hot edit), for saving into the builder's copy after
 * a Ctrl+S save. Same filter as the request copy, without its caps (the backend applies its own).
 */
function collectFounderEditsForBuilder(): Record<string, string> {
  const dirty = workbenchStore.getModifiedFiles();
  const out: Record<string, string> = {};
  for (const path of Object.keys(dirty)) {
    if (path && !workbenchManualPathDisallowed(path) && !isHotEditBridgePath(path)) {
      out[path] = dirty[path];
    }
  }
  return out;
}

function collectWorkbenchManualFilesForRequest(): Record<string, string> | undefined {
  const dirty = workbenchStore.getModifiedFiles();
  const dirtyPaths = Object.keys(dirty).filter((p) => p && !workbenchManualPathDisallowed(p));
  if (dirtyPaths.length === 0) {
    workbenchStore.manualContextTruncationWarning.set(null);
    return undefined;
  }

  const bridgeExtras = HOT_EDIT_BRIDGE_REL_PATHS.filter((p) => !dirtyPaths.includes(p));
  const mergedPaths = [...bridgeExtras, ...dirtyPaths.sort()];
  const cappedPaths = mergedPaths.slice(0, WORKBENCH_MANUAL_MAX_FILES);
  const out: Record<string, string> = {};
  let total = 0;
  let truncatedByFiles = mergedPaths.length > WORKBENCH_MANUAL_MAX_FILES;
  let truncatedByChars = false;
  let perFileTruncated = false;

  for (const path of cappedPaths) {
    let body = dirty[path];
    if (body === undefined && isHotEditBridgePath(path)) {
      body = getMergedFileContent(path);
    }
    if (body === undefined) body = '';
    const originalLen = body.length;
    if (body.length > WORKBENCH_MANUAL_MAX_FILE_CHARS) {
      body = `${body.slice(0, WORKBENCH_MANUAL_MAX_FILE_CHARS)}\n\n...[truncated]\n\n`;
      perFileTruncated = true;
    }
    if (total + body.length > WORKBENCH_MANUAL_MAX_TOTAL_CHARS) {
      truncatedByChars = true;
      break;
    }
    out[path] = body;
    total += body.length;
    if (originalLen > WORKBENCH_MANUAL_MAX_FILE_CHARS) {
      perFileTruncated = true;
    }
  }

  if (Object.keys(out).length < dirtyPaths.length) {
    truncatedByFiles = true;
  }

  updateManualContextTruncationWarning(dirtyPaths.length, Object.keys(out).length, {
    truncatedByFiles,
    truncatedByChars,
    perFileTruncated,
  });

  return Object.keys(out).length > 0 ? out : undefined;
}

function updateManualContextTruncationWarning(
  dirtyCount: number,
  includedCount: number,
  flags: { truncatedByFiles: boolean; truncatedByChars: boolean; perFileTruncated: boolean },
): void {
  const parts: string[] = [];
  if (flags.truncatedByFiles && dirtyCount > includedCount) {
    parts.push(`only ${includedCount} of ${dirtyCount} edited files were sent to the AI`);
  } else if (flags.truncatedByFiles) {
    parts.push(`manual edit context capped at ${WORKBENCH_MANUAL_MAX_FILES} files`);
  }
  if (flags.truncatedByChars) {
    parts.push('total manual-edit payload hit the size limit');
  }
  if (flags.perFileTruncated) {
    parts.push('some file bodies were truncated');
  }
  if (parts.length === 0) {
    workbenchStore.manualContextTruncationWarning.set(null);
    return;
  }
  workbenchStore.manualContextTruncationWarning.set(
    `Manual edit context was capped: ${parts.join('; ')}. The AI may not see all of your changes.`,
  );
}

function collectWorkbenchManualFileHashesForRequest(
  manualFiles?: Record<string, string>,
): Record<string, string> | undefined {
  if (!manualFiles || Object.keys(manualFiles).length === 0) return undefined;
  const dirty = workbenchStore.getModifiedFiles();
  const out: Record<string, string> = {};
  for (const path of Object.keys(manualFiles).sort()) {
    const body = manualFiles[path] ?? '';
    out[path] = hashFileContent(body);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function collectWorkbenchManualFileDiffsForRequest(): Record<string, string> | undefined {
  const dirty = workbenchStore.getModifiedFiles();
  const paths = Object.keys(dirty).filter((p) => p && !workbenchManualPathDisallowed(p));
  if (paths.length === 0) return undefined;
  paths.sort();
  const cappedPaths = paths.slice(0, WORKBENCH_MANUAL_MAX_FILES);
  const out: Record<string, string> = {};
  let total = 0;
  for (const path of cappedPaths) {
    const baseline = workbenchStore.getLastToolWritten(path);
    if (baseline === undefined) continue;
    const curr = dirty[path] ?? '';
    const diffSnippet = strippedUnifiedDiff(path, baseline, curr);
    if (!diffSnippet) continue;
    let clipped = diffSnippet;
    if (clipped.length > WORKBENCH_MANUAL_DIFF_MAX_FILE_CHARS) {
      clipped = `${clipped.slice(0, WORKBENCH_MANUAL_DIFF_MAX_FILE_CHARS)}\n\n...[diff truncated]\n\n`;
    }
    if (total + clipped.length > WORKBENCH_MANUAL_DIFF_MAX_TOTAL_CHARS) {
      break;
    }
    out[path] = clipped;
    total += clipped.length;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function workbenchBrowserPersistKey(chatId: string): string {
  return `ideoz:wbt:v1:${chatId}`;
}

/** Debounce POST/PATCH build-session while tool steps stream. */
const BUILD_SESSION_PATCH_DEBOUNCE_MS = 450;
/** Debounce after file edits before uploading a ZIP snapshot. */
const AUTO_SNAPSHOT_FILE_DEBOUNCE_MS = 4_000;
/** Min gap between *file-edit* snapshots (separate from build/stream cooldown). */
const AUTO_SNAPSHOT_COOLDOWN_FILES_MS = 5_000;
/** Min gap after a build- or stream-end snapshot (kept short; separate from file cooldown). */
const AUTO_SNAPSHOT_COOLDOWN_BUILD_STREAM_MS = 2_000;
/** How long a forced snapshot waits out `restoreInProgress`'s trailing flag before giving up. */
const SNAPSHOT_RESTORE_WAIT_MS = 8_000;
/** How long a forced snapshot waits behind an already-in-flight upload before proceeding anyway. */
const SNAPSHOT_CHAIN_MAX_WAIT_MS = 30_000;
/** Bound on a locally-resumed build before it's treated as stalled. */
const RESUME_BUILD_MAX_MS = 240_000;
const MAX_BUILD_REPEAT_ERRORS = 3;

/**
 * P6.4d-3: Chef runs writeFile/searchReplace/deleteFile itself and the browser mirrors each call,
 * so a turn no longer needs a browser round per file write. Off until measured in staging.
 */
const SERVER_WRITES_ENABLED = import.meta.env?.VITE_WORKBENCH_SERVER_WRITES === 'true';
const SERVER_WRITE_TOOLS: ReadonlySet<string> = new Set(['writeFile', 'searchReplace', 'deleteFile']);

/**
 * Light-preview plan F1: Chef runs `build` (pnpm install, vite build and tsc) instead of this
 * tab. Chef checks the files it holds for the turn, which are only current when the writes ran
 * there too, so this follows `serverWrites`.
 */
const SERVER_BUILD_ENABLED =
  import.meta.env?.VITE_WORKBENCH_SERVER_BUILD === 'true' && SERVER_WRITES_ENABLED;

const LEASE_HELD_ELSEWHERE_MESSAGE =
  'This prototype is building in another tab or on another device. It shows here when it finishes.';
const MAX_TRUNCATION_CONTINUES = 3;
const MAX_RETAINED_TOOL_STEPS = 200;
const TOOL_STEPS_PERSIST_DEBOUNCE_MS = 300;

function capToolSteps(steps: ToolStep[]): ToolStep[] {
  if (steps.length <= MAX_RETAINED_TOOL_STEPS) return steps;
  return steps.slice(steps.length - MAX_RETAINED_TOOL_STEPS);
}

/**
 * Waits for this tab's WebContainer lock decision (`workbenchStore.tabOwnership`, set when the
 * boot asks for the lock). Resolves with the state after `timeoutMs` even if still `unknown`,
 * so a browser without Web Locks, or a boot that never started, does not block the build.
 */
function waitForTabOwnershipDecision(timeoutMs = 5_000): Promise<WorkbenchTabOwnership> {
  const current = workbenchStore.tabOwnership.get();
  if (current !== 'unknown') return Promise.resolve(current);
  return new Promise((resolve) => {
    let unsubscribe = (): void => {};
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(workbenchStore.tabOwnership.get());
    }, timeoutMs);
    unsubscribe = workbenchStore.tabOwnership.listen((state) => {
      if (state === 'unknown') return;
      clearTimeout(timer);
      unsubscribe();
      resolve(state);
    });
  });
}

export function usePrototyperWorkbenchFacade(options: UsePrototyperWorkbenchFacadeOptions) {
  const {
    conversationId,
    projectId,
    tmpConversationId,
    modelProvider,
    modelChoice,
    onCreditsRefresh,
    onProjectCreated,
    onStreamDemoLinkId,
    streamingBuildLlmInteractionIdRef,
    streamingLlmInteractionId,
    getPrototypeDisplayName,
    onPatchMessageByLlmInteractionId,
    onBuildStreamTurnCompleted,
    onSnapshotRestored,
    hasPrototypeCredits,
    onAutoFixReady,
    needsImmediateWorkbenchBoot = false,
    streamingWorkbenchPrototypePanelVisible = false,
    preferredRestoreInteractionId = null,
  } = options;

  const accessToken = useAccessToken();
  const webcontainerRef = useRef<WebContainer | null>(null);
  /** Preview recovery (`recoverPreviewEnvironment`) can replace the instance; follow it. */
  useEffect(
    () =>
      workbenchStore.webcontainer.listen((wc) => {
        // nanostores types the value as Readonly; it is the same instance the store holds.
        if (wc) webcontainerRef.current = wc as WebContainer;
      }),
    []
  );
  const processedAnnotationsRef = useRef(new Set<string>());
  const writtenAssetPathsRef = useRef(new Set<string>());
  const assetWriteChainRef = useRef(Promise.resolve());
  const [buildTimeline, setBuildTimeline] = useState<BuildTimelineV2 | null>(null);
  const buildTimelineRef = useRef<BuildTimelineV2 | null>(null);
  /** Annotation overlay (planComplete / postBuild) — preserved when toolSteps rebuild timeline. */
  const timelineAnnotationRef = useRef<BuildTimelineV2 | null>(null);
  const buildSessionPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const buildSessionPrototypeMetaRef = useRef<{
    prototypeVersionIndex?: number | null;
    prototypeDisplayName?: string | null;
  }>({});
  const [toolSteps, setToolSteps] = useState<ToolStep[]>([]);
  const toolStepsRef = useRef<ToolStep[]>([]);
  /** The last server build passed, but Chef sent the model back to fix design rules. */
  const [designFixPending, setDesignFixPending] = useState(false);
  /** Bumps when `chefProjectIdRef` changes without `toolSteps`, so sessionStorage stays in sync. */
  const [persistVersion, setPersistVersion] = useState(0);
  const [snapshotBootstrap, setSnapshotBootstrap] = useState<WorkbenchSnapshotBootstrap>('pending');
  const [snapshotRestoreError, setSnapshotRestoreError] = useState<string | null>(null);
  /** Shown when bootstrap restored an earlier version because the latest file was missing. */
  const [snapshotRestoreNotice, setSnapshotRestoreNotice] = useState<string | null>(null);
  /** True from a "Retry restore" click until bootstrap settles; keeps the workbench mounted. */
  const [snapshotRestoreRetrying, setSnapshotRestoreRetrying] = useState(false);
  const [warmBootLatch, setWarmBootLatch] = useState(0);
  const bootStatus = useStore(workbenchStore.bootStatus);
  const buildStatus = useStore(workbenchStore.buildStatus);

  const workbenchWanted =
    needsImmediateWorkbenchBoot ||
    streamingWorkbenchPrototypePanelVisible ||
    warmBootLatch > 0;

  /**
   * F2: with `VITE_WORKBENCH_PREVIEW=light` the prototype is compiled in the tab instead of run
   * in a WebContainer. The runtime reports `unavailable` when its dependency bundle is missing,
   * and the WebContainer then boots as before — so the two never run at the same time, and a
   * missing bundle costs the founder nothing.
   */
  const lightPreview = useLightPreviewRuntime(LIGHT_PREVIEW_REQUESTED && workbenchWanted);
  const lightPreviewActive =
    LIGHT_PREVIEW_REQUESTED && workbenchWanted && lightPreviewOwnsPreview(lightPreview.status);
  const shouldBootWebContainer = workbenchWanted && !lightPreviewActive;
  /**
   * Where files have to land: the light preview's store, or a WebContainer's filesystem. Read
   * from callbacks and effects rather than from the rendered value they closed over.
   *
   * `workbenchWanted` is deliberately not part of this one. A version click raises that flag and
   * starts restoring in the same tick, so a value that only turns true on the next commit sent
   * the click down the WebContainer path — where it waited 45 s for a container nobody was
   * booting, then failed with "WebContainer did not become ready in time".
   */
  const lightPreviewRuntimeRef = useRef(false);
  lightPreviewRuntimeRef.current =
    LIGHT_PREVIEW_REQUESTED && lightPreviewOwnsPreview(lightPreview.status);

  /**
   * Applies one downloaded version to whichever runtime owns the preview: the WebContainer's
   * filesystem, or — with the light preview — the store the in-tab compiler reads. `false`
   * means no runtime was there to take it, the caller's "nothing was restored" signal.
   */
  const applySnapshotBlobToRuntime = useCallback(
    async (blob: Blob, signal: AbortSignal): Promise<boolean> => {
      if (lightPreviewRuntimeRef.current) {
        await applySnapshotZipToStore(blob, signal);
        return true;
      }
      const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
      if (!wc) return false;
      await waitForTemplateReady();
      const applied = await applySnapshotZipToWebContainer(wc, blob, signal);
      // A fast switch kept the dev server running; only a full restore restarts it.
      if (applied.mode === 'restart') restartDevServerAfterSnapshot(wc);
      return true;
    },
    [],
  );

  const accessTokenRef = useRef(accessToken);
  const modelRef = useRef({ modelProvider, modelChoice });
  const chefProjectIdRef = useRef<string | null>(null);
  /**
   * The turn's prototype version on the backend (P6.4a): draft + build lease at turn start,
   * verified with the verified snapshot, failed when a turn ends without a passing build.
   * Every call degrades to a no-op while the versions API is unavailable.
   */
  /** Version changes posted to, and received from, this browser's other tabs (P6.4e). */
  const versionEventsRef = useRef<VersionEvents | null>(null);
  if (!versionEventsRef.current) {
    versionEventsRef.current = createVersionEvents(browserVersionEventChannel());
  }
  const versionTrackerRef = useRef<VersionTracker | null>(null);
  /** Re-read this conversation's versions; set once the refresh state below exists. */
  const refreshTurnVersionsRef = useRef<() => void>(() => {});
  if (!versionTrackerRef.current) {
    versionTrackerRef.current = createVersionTracker({
      api: prototyperService,
      owner: getWorkbenchTabId(),
      onVersionChange: (ctx, status) => {
        versionEventsRef.current?.publish({
          conversationId: ctx.conversationId,
          llmInteractionId: ctx.llmInteractionId,
          status,
        });
        // The event above reaches other tabs only. This tab re-reads too: a version built
        // after the page loaded was missing from `turnVersions` until a reload, and the
        // Publish menu, which looks the version on screen up there, said "Checking this
        // version..." forever.
        refreshTurnVersionsRef.current();
      },
    });
  }
  /**
   * The number the backend gave a version when it was verified goes on its turn's message, so
   * the pill shows it right away (the versions list follows on its next read).
   */
  const recordVersionNumber = (
    llmInteractionId: string,
    version: { number?: number | null } | null | undefined,
  ): void => {
    if (typeof version?.number === 'number' && version.number > 0) {
      onPatchMessageByLlmInteractionId?.(llmInteractionId, {
        prototypeVersionIndex: version.number,
      });
    }
  };
  const versionTurnContext = (llmInteractionId: string | null | undefined) => {
    const { projectId: pid, conversationId: cid } = projectConversationRef.current;
    return pid && cid && llmInteractionId
      ? { projectId: pid, conversationId: cid, llmInteractionId }
      : null;
  };
  /**
   * Automatic fixing (P6.4b). `fixTurnIdRef`: the turn whose next build-stream is an automatic
   * fix, sent as `turnKind: fix` (the AI API claims one of the version's attempts and does not
   * bill it). `versionAttention`: the version waiting for the founder (the card).
   */
  const fixTurnIdRef = useRef<string | null>(null);
  /**
   * "Fix with AI" on a crashed preview (test finding 5): the turn being repaired and the repair
   * its build carries (`repair`, Chef's RepairPayload). Sent instead of a chat message, which
   * Nexus planned as an ordinary request and answered with a question the founder could not
   * answer. The AI API reads `repair` on round 1 only; cleared when the turn ends.
   */
  const crashRepairRef = useRef<{
    llmInteractionId: string;
    repair: PreviewCrashRepair;
    /** The founder's hand edits as they were when the repair started (see `repairPreviewCrash`). */
    manual?: Record<string, string>;
    manualDiffs?: Record<string, string>;
    manualHashes?: Record<string, string>;
  } | null>(null);
  /**
   * What Chef reported it executes this turn (`serverExecution` annotation), not what this tab
   * asked for. Both stay false until Chef says otherwise, so an older Chef — one that ignores
   * `serverWrites` / `serverBuild` — simply leaves every tool to the browser.
   */
  const serverWritesTurnRef = useRef(false);
  const serverBuildTurnRef = useRef(false);
  /** Chef-executed writes the browser could not mirror this turn; reported with the next build. */
  const serverWriteDriftRef = useRef<string[]>([]);
  /** How Chef delivers its writes this turn: streamed annotations, or the browser's tool handler. */
  const serverWriteDeliveryRef = useRef<'annotation' | 'client'>('annotation');
  /** Per-turn 25-minute budget, fix attempts included (P6.5). */
  const turnBudgetsRef = useRef(createTurnBudgets());
  /** Set once `restorePrototypeSnapshotForInteraction` / `runAutoPrototypeSnapshot` exist (below). */
  const restoreSnapshotRef = useRef<
    (llmInteractionId: string, hint?: PrototypeSnapshotRestoreHint) => Promise<unknown>
  >(async () => undefined);
  const runAutoSnapshotRef = useRef<
    (reason: AutoSnapshotReason, options?: { force?: boolean; overwrite?: boolean }) => Promise<unknown>
  >(async () => undefined);
  /** Wakes a waiting continuation scheduler when chat status or client tools change (FE-7). */
  const continuationWakersRef = useRef(new Set<() => void>());
  const wakeStreamContinuation = (): void => {
    continuationWakersRef.current.forEach((wake) => wake());
  };
  /** Set once `stopAndAbortGeneration` exists (defined further down). */
  const stopAndAbortRef = useRef<() => void>(() => {});
  const hasPrototypeCreditsRef = useRef(hasPrototypeCredits);
  hasPrototypeCreditsRef.current = hasPrototypeCredits;
  const onAutoFixReadyRef = useRef(onAutoFixReady);
  onAutoFixReadyRef.current = onAutoFixReady;
  const [versionAttention, setVersionAttention] = useState<VersionAttention | null>(null);
  const [versionAttentionCanUndo, setVersionAttentionCanUndo] = useState(false);
  const [versionAttentionBusy, setVersionAttentionBusy] = useState<'retry' | 'undo' | null>(null);
  const [autoFixLabel, setAutoFixLabel] = useState<string | null>(null);
  /**
   * After a code failure was recorded on the turn's version: start the next automatic fix, or
   * show the card. `none` when the versions API did not answer (callers keep their
   * pre-versions behaviour) or the conversation changed. Callers check that their turn is
   * still current first.
   */
  const handleVersionFailure = (
    ctx: { conversationId: string; llmInteractionId: string },
    failure: TrackedVersionFailure | null
  ): AfterFailureAction => {
    if (!failure) return 'none';
    if (projectConversationRef.current.conversationId !== ctx.conversationId) return 'none';
    fixTurnIdRef.current = null;
    const action = decideAfterVersionFailure({
      result: failure,
      hasCredits: hasPrototypeCreditsRef.current?.() ?? true,
    });
    if (action === 'none' || (action === 'fix' && !onAutoFixReadyRef.current)) return 'none';
    if (action === 'fix' && turnBudgetsRef.current.expired(ctx.llmInteractionId, Date.now())) {
      expireTurnBudgetRef.current(ctx.llmInteractionId);
      return 'needs_input';
    }
    if (action === 'fix') {
      fixTurnIdRef.current = ctx.llmInteractionId;
      setVersionAttention(null);
      setAutoFixLabel(fixProgressLabel(failure.fixAttempts + 1, failure.fixAttemptsGranted));
      onAutoFixReadyRef.current?.(ctx.llmInteractionId);
      return action;
    }
    setVersionAttention({
      llmInteractionId: ctx.llmInteractionId,
      versionId: failure.versionId,
      fixAttempts: failure.fixAttempts,
      fixAttemptsGranted: failure.fixAttemptsGranted,
      notice: action === 'no_credits' ? NO_CREDITS_FOR_FIX_NOTICE : null,
    });
    return action;
  };
  const handleVersionFailureRef = useRef(handleVersionFailure);
  handleVersionFailureRef.current = handleVersionFailure;

  /**
   * The founder stopped a turn before its build passed (`stoppedTurnOutcome` = `unfinished`).
   * The version stays a draft that Continue finishes. Three things the Stop left wrong:
   * - `buildStatus` stayed `building`: the build tool leaves it alone once its turn is superseded,
   *   which Stop makes it, so the panel said "Building and checking your prototype..." for good.
   * - The workspace holds the stopped turn's unbuilt files while the page still shows the previous
   *   version as the one on screen: that version is put back.
   * - The versions list did not have the new draft, so its pill offered no "Didn't finish" and no
   *   Continue, and its timeline said "Built in Ns", until a reload.
   */
  const settleStoppedBuild = (stoppedTurnId: string | null | undefined): void => {
    if (workbenchStore.buildStatus.get() === 'building') {
      workbenchStore.buildStatus.set('idle');
    }
    const shown = activeSnapshotInteractionIdRef.current;
    if (shown && shown !== stoppedTurnId) {
      // `force`: `shown` is already the version on screen, so a plain restore returns at once and
      // the stopped turn's files stayed in the preview under the previous version's title.
      void restoreSnapshotRef.current(shown, { force: true }).catch((err) => {
        console.warn('[Workbench] restoring the version on screen after Stop failed', err);
      });
    }
    refreshTurnVersionsRef.current();
  };

  /**
   * "Fix with AI" on a crashed preview: rebuild `llmInteractionId`'s version with the crash as
   * its repair, the way an automatic build fix repairs a failed version in place. The founder's
   * unsaved editor changes go with the build as usual (`workbenchManualFiles`) and are named in
   * the repair, so the model repairs the code that actually crashed. Billed as an ordinary edit.
   *
   * Returns false when it cannot start (a build is running, or the page does not wire builds);
   * the caller then falls back to the chat message.
   */
  const repairPreviewCrash = useCallback(
    (llmInteractionId: string, crash: WorkbenchPreviewCrash): boolean => {
      if (!onAutoFixReadyRef.current) return false;
      if (workbenchStore.isStreaming.get() || workbenchStore.clientToolsBusy.get()) return false;
      const changedFiles = Object.keys(workbenchStore.getModifiedFiles());
      const manual = collectWorkbenchManualFilesForRequest();
      crashRepairRef.current = {
        llmInteractionId,
        repair: previewCrashRepair(crash, changedFiles),
        manual,
        manualDiffs: collectWorkbenchManualFileDiffsForRequest(),
        manualHashes: collectWorkbenchManualFileHashesForRequest(manual),
      };
      // The crashed code is usually the founder's own edit, which the workbench otherwise
      // protects from the model: a write to a hand-edited file is set aside as a conflict and
      // the founder's version kept. That kept the crash (seen on local: the fix was undone on
      // reload). The edits go to the builder with the repair (captured above); from here they
      // are the version's content, which the repair is free to change.
      workbenchStore.clearManualEditConsumedFlags();
      fixTurnIdRef.current = null;
      setAutoFixLabel('Fixing the crash…');
      onAutoFixReadyRef.current(llmInteractionId);
      return true;
    },
    []
  );
  /**
   * The build could not run because the WebContainer stopped responding. The code is not at
   * fault, so the model gets no new round and no automatic fix starts: stop the turn, keep the
   * version a draft (`kind: environment`), recover the workspace, then finish the build locally.
   */
  const endTurnAfterEnvironmentFailure = (llmInteractionId: string | null | undefined): void => {
    cancelStreamContinuationRef.current?.();
    cancelStreamContinuationRef.current = null;
    toolResultContinuationPendingRef.current = false;
    workbenchStore.isStreaming.set(false);
    workbenchStore.buildStatus.set('error');
    fixTurnIdRef.current = null;
    setAutoFixLabel(null);
    setWorkbenchPersistError(
      'The preview workspace stopped responding, so this build could not finish. It is restarting; your files are saved.'
    );
    const endedTimeline = markStreamEndedWithoutSuccessfulBuild(
      buildTimelineRef.current ?? createEmptyTimeline()
    );
    buildTimelineRef.current = endedTimeline;
    setBuildTimeline(endedTimeline);

    const ctx = versionTurnContext(llmInteractionId);
    if (ctx) void versionTrackerRef.current?.failed(ctx, { kind: 'environment' });
    void recoverPreviewEnvironment('workspace-unresponsive').then((recovered) => {
      if (!recovered || !ctx || !llmInteractionId) return;
      if (projectConversationRef.current.conversationId !== ctx.conversationId) return;
      setWorkbenchPersistError(null);
      // The recovered container has this turn's files (written back from the store).
      void resumeInterruptedBuildRef.current(llmInteractionId, { filesApplied: true });
    });
  };
  const endTurnAfterEnvironmentFailureRef = useRef(endTurnAfterEnvironmentFailure);
  endTurnAfterEnvironmentFailureRef.current = endTurnAfterEnvironmentFailure;
  /**
   * Chef's server-side build reported progress (F1). It replaces what the browser's own `build`
   * tool call used to publish — the timeline step, `buildStatus`, the repeated-error counter and
   * the turn's snapshots — so the rest of the turn (fix loop, version verify) is unchanged.
   */
  const handleServerBuildEvent = (event: Record<string, unknown>): void => {
    const attempt = typeof event.attempt === 'number' ? event.attempt : 1;
    const stepKey = `server-build-${attempt}`;
    const lid = streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId;

    if (event.phase === 'running') {
      workbenchStore.buildStatus.set('building');
      setToolSteps((prev) => {
        if (prev.some((s) => s.key === stepKey)) return prev;
        const next = capToolSteps([
          ...prev,
          { key: stepKey, toolName: 'build', args: {}, isComplete: false },
        ]);
        toolStepsRef.current = next;
        return next;
      });
      const ctx = versionTurnContext(lid);
      if (ctx) {
        void versionTrackerRef.current?.begin(ctx, chefProjectIdForTurn(ctx.llmInteractionId));
      }
      // Only when the browser holds the files. With server writes it may not have them all yet,
      // and snapshotting then would store the starter template as this turn's version — which is
      // what made a finished turn report "No saved version of this prototype yet".
      if (lid && !serverWritesTurnRef.current) {
        void runAutoPrototypeSnapshot('build-start', {
          buildVerified: false,
          force: true,
          llmInteractionId: lid,
        }).catch((err) => console.warn('[Workbench] pre-build snapshot failed', err));
      }
      return;
    }

    if (event.phase !== 'done') return;

    if (event.environment === true) {
      // Chef could not run the build (template copy or dependency install). The draft stays, no
      // fix attempt is spent, and the model is not asked to rewrite working code.
      const message =
        typeof event.errors === 'string' && event.errors.trim()
          ? `The build could not run on the server:\n${event.errors}`
          : 'The build could not run on the server.';
      serverBuildEnvironmentFailureRef.current = true;
      workbenchStore.buildStatus.set('error');
      setToolSteps((prev) => {
        const exists = prev.some((s) => s.key === stepKey);
        const next = capToolSteps(
          exists
            ? prev.map((s) => (s.key === stepKey ? { ...s, result: message, isComplete: true } : s))
            : [...prev, { key: stepKey, toolName: 'build', args: {}, isComplete: true, result: message }],
        );
        toolStepsRef.current = next;
        return next;
      });
      cancelStreamContinuationRef.current?.();
      cancelStreamContinuationRef.current = null;
      toolResultContinuationPendingRef.current = false;
      workbenchStore.isStreaming.set(false);
      fixTurnIdRef.current = null;
      setAutoFixLabel(null);
      setWorkbenchPersistError(
        'The build could not run on the server. Your files are saved — try Rebuild in a moment.'
      );
      const envCtx = versionTurnContext(lid);
      if (envCtx) void versionTrackerRef.current?.failed(envCtx, { kind: 'environment' });
      return;
    }

    const ok = event.ok === true;
    // Chef sent the model back to fix mobile design rules after this passing build: the turn is
    // not ending, so it is not shown as finalizing (bookstore v4 sat on "Finalizing" while the
    // model rewrote five screens, 1 Oct 2026). The build still passed: its files are verified
    // below, and a turn that ends here still ends as a success.
    setDesignFixPending(ok && event.designFix === true);
    const errors = typeof event.errors === 'string' ? event.errors : '';
    const result = ok ? 'Build succeeded.' : `Build failed:\n${errors}`;
    workbenchStore.buildStatus.set(ok ? 'success' : 'error');

    if (ok) {
      workbenchStore.hasUnbuiltEdits.set(false);
      buildRepeatRef.current = { lastSig: '', count: 0 };
      if (lid) {
        void (async () => {
          // The build passed on the server, so its files are the verified ones: make sure the
          // workspace has them before this snapshot records what the browser holds.
          if (serverWritesTurnRef.current) {
            // Writes still being applied land first; a file the build listed that is still
            // missing never arrived, and is fetched by the sync below.
            await assetWriteChainRef.current.catch(() => undefined);
            const missing = missingServerBuiltFiles(event.paths, workbenchStore.files.get());
            if (missing.length > 0) {
              console.warn('[Workbench] the server built with files this tab does not have', missing);
              serverWriteDriftRef.current.push(...missing);
            }
            await syncTurnFilesFromServerRef.current(lid);
          }
          await runAutoPrototypeSnapshot('build', {
            buildVerified: true,
            force: true,
            llmInteractionId: lid,
          });
        })();
      }
    } else {
      const sig = typeof event.errorSig === 'string' ? event.errorSig : '';
      const rep = buildRepeatRef.current;
      if (sig && sig === rep.lastSig) {
        rep.count++;
      } else {
        rep.lastSig = sig;
        rep.count = 1;
      }
    }

    setToolSteps((prev) => {
      const exists = prev.some((s) => s.key === stepKey);
      const next = capToolSteps(
        exists
          ? prev.map((s) => (s.key === stepKey ? { ...s, result, isComplete: true } : s))
          : [...prev, { key: stepKey, toolName: 'build', args: {}, isComplete: true, result }],
      );
      toolStepsRef.current = next;
      return next;
    });
  };
  /** This turn's server build failed on the host, so its end must not be read as a code failure. */
  const serverBuildEnvironmentFailureRef = useRef(false);
  /**
   * Puts the files Chef wrote this turn into the workspace, reading them back from the version.
   * The streamed `serverWrite` annotations normally do this as the turn runs; this is the
   * authoritative catch-up, so a dropped annotation — or a browser older than Chef — can never
   * leave the code view empty and the preview on the starter template. A hand edit always wins.
   */
  const syncTurnFilesFromServer = async (
    llmInteractionId: string | null | undefined,
  ): Promise<number> => {
    const ctx = versionTurnContext(llmInteractionId);
    if (!ctx) return 0;
    // Nothing to catch up on when the annotations delivered and no write was reported missing.
    // Asking anyway answered 409 ("only the newest version of a prototype has its files in the
    // builder") for every turn whose version had since been superseded — a red line in the
    // console for a request that had nothing to fetch.
    if (
      serverWriteDeliveryRef.current === 'annotation' &&
      serverWriteDriftRef.current.length === 0 &&
      Object.keys(workbenchStore.files.get()).length > 0
    ) {
      return 0;
    }
    try {
      const version =
        turnVersionsRef.current.get(ctx.llmInteractionId) ??
        (await versionTrackerRef.current?.current(ctx)) ??
        null;
      if (!version) return 0;

      const { files } = await prototyperService.getPrototypeVersionFiles(
        ctx.projectId,
        ctx.conversationId,
        version.id,
      );
      const entries = Object.entries(files ?? {});
      if (entries.length === 0) return 0;

      const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
      let applied = 0;
      for (const [path, content] of entries) {
        if (typeof content !== 'string') continue;
        const existing = workbenchStore.files.get()[path];
        if (existing?.modified) continue;
        if (existing?.content === content) continue;
        if (wc) {
          await persistUserFile(wc, path, content, false);
        } else {
          // No container (the light preview): the store is the whole workspace.
          workbenchStore.files.setKey(path, { content, modified: false });
          workbenchStore.recordToolWriteSnapshot(path, content);
        }
        applied++;
      }
      if (applied > 0) {
        console.info('[Workbench] took this turn\'s files from the server', { applied });
      }
      return applied;
    } catch (err) {
      console.warn('[Workbench] syncing this turn\'s files from the server failed', err);
      return 0;
    }
  };
  const syncTurnFilesFromServerRef = useRef(syncTurnFilesFromServer);
  syncTurnFilesFromServerRef.current = syncTurnFilesFromServer;
  const handleServerBuildEventRef = useRef(handleServerBuildEvent);
  handleServerBuildEventRef.current = handleServerBuildEvent;
  /**
   * The turn used its 25-minute budget: stop the stream and the Chef generation, and let the
   * version wait for the founder (reason `timeout`).
   */
  const expireTurnBudget = (llmInteractionId: string): void => {
    turnBudgetsRef.current.forget(llmInteractionId);
    fixTurnIdRef.current = null;
    setAutoFixLabel(null);
    // The turn's build already passed: its version is verified, so running out of time ends it
    // as a success, the way Stop does (`stoppedTurnOutcome` = `build-passed`), never as failed.
    const buildPassed =
      workbenchStore.buildStatus.get() === 'success' && lastToolStepBuildPassed(toolStepsRef.current);
    if (workbenchStore.isStreaming.get()) stopAndAbortRef.current();
    if (buildPassed) return;
    const ctx = versionTurnContext(llmInteractionId);
    if (!ctx) {
      setWorkbenchPersistError(TURN_BUDGET_MESSAGE);
      return;
    }
    void versionTrackerRef.current
      ?.failed(ctx, {
        kind: 'code',
        reason: 'timeout',
        errorSignature: TURN_BUDGET_SIGNATURE,
        buildLog: lastBuildStepOutput(toolStepsRef.current),
      })
      .then((failure) => {
        if (projectConversationRef.current.conversationId !== ctx.conversationId) return;
        if (!failure) {
          setWorkbenchPersistError(TURN_BUDGET_MESSAGE);
          return;
        }
        setVersionAttention({
          llmInteractionId,
          versionId: failure.versionId,
          fixAttempts: failure.fixAttempts,
          fixAttemptsGranted: failure.fixAttemptsGranted,
          reason: 'timeout',
        });
      });
  };
  const expireTurnBudgetRef = useRef(expireTurnBudget);
  expireTurnBudgetRef.current = expireTurnBudget;
  /** Code + message from a `data-prototyperError` part; consumed by the next `onError`. */
  const pendingStreamErrorPartRef = useRef<BuildStreamError | null>(null);
  /** Last classified build-stream failure of this turn (read by `onFinish`). */
  const lastStreamErrorRef = useRef<BuildStreamError | null>(null);
  /** Transport retries already scheduled this turn (bounded by MAX_BUILD_STREAM_TRANSPORT_RETRIES). */
  const transportRetryCountRef = useRef(0);
  const buildRepeatRef = useRef({ lastSig: '', count: 0 });
  const lastFileSnapshotAtRef = useRef(0);
  const lastBuildStreamSnapshotAtRef = useRef(0);
  const autoSnapshotInFlightRef = useRef(false);
  const autoSnapshotChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const resumeBuildInFlightRef = useRef<string | null>(null);
  const resumeBuildAttemptedRef = useRef<Set<string>>(new Set());
  const resumeBuildAbortRef = useRef<AbortController | null>(null);
  const projectConversationRef = useRef({
    projectId: projectId as string | undefined,
    conversationId: conversationId as string | undefined,
  });
  const fileSnapshotDebounceRef = useRef<number | undefined>(undefined);
  const pendingFileSnapshotAfterBusyRef = useRef(false);
  const fileSnapshotCooldownRetryTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const lastSnapshotFingerprintByInteractionRef = useRef<Map<string, string>>(new Map());
  const bootstrapAttemptedKeyRef = useRef<string | undefined>(undefined);

  type AddToolOutputArgs =
    | { tool: string; toolCallId: string; state?: 'output-available'; output: unknown }
    | { tool: string; toolCallId: string; state: 'output-error'; errorText: string };

  const addToolOutputRef = useRef<
    ((args: AddToolOutputArgs) => void | PromiseLike<void>) | null
  >(null);

  /** Latest `sendMessage` for `onFinish` / post-tool continuation (SDK callbacks run via ref). */
  const sendMessageRef = useRef<((msg?: unknown) => void) | null>(null);
  const messagesRef = useRef<readonly unknown[]>([]);
  const continueScheduleGenRef = useRef(0);
  /** Cancels an in-flight continuation scheduler when a new one starts. */
  const cancelStreamContinuationRef = useRef<(() => void) | null>(null);
  /** Avoid duplicate POSTs when onFinish and post-tool callbacks race. */
  const continuationSentRef = useRef(false);
  /** Client tool output must be POSTed once; cleared after send or successful turn end. */
  const toolResultContinuationPendingRef = useRef(false);
  /** Latest useChat `status` — continuation must not POST while assistant stream is still open. */
  const chatStatusRef = useRef<string>('ready');
  /** Last turn ended on `finishReason: "length"` (reported by Chef's usage annotation). */
  const streamTruncatedRef = useRef(false);
  /** Bounds how many times a truncated turn may be auto-resumed. */
  const truncationContinueRef = useRef(0);
  /** Bounds how many build-stream POST continuations one turn may trigger. */
  const toolContinuationCountRef = useRef(0);
  /** planComplete.files paths for the active edit turn (drives completeness + continuation cap). */
  const editPlannedFilePathsRef = useRef<string[]>([]);

  const turnSessionRef = useRef<TurnSession>(createTurnSession());
  /**
   * Mirrors `turnSessionRef`'s aborted state as a real `AbortSignal` so tool-adapter
   * calls (`executeWriteFile`/`executeBuild`/…) can bail out of their own in-flight
   * `wc.fs.writeFile`/`wc.spawn` calls instead of only having their *output* suppressed
   * after the fact — closes the window where a stale turn's write/build still lands on
   * the shared WebContainer after the user has switched to a different conversation.
   */
  const turnAbortControllerRef = useRef<AbortController>(new AbortController());
  const abortCurrentTurn = useCallback((): void => {
    bumpTurnGeneration(turnSessionRef.current, true);
    turnAbortControllerRef.current.abort();
    turnAbortControllerRef.current = new AbortController();
    // The lease expires on the server; another tab or a reload can take the draft over.
    versionTrackerRef.current?.stop();
    fixTurnIdRef.current = null;
    setAutoFixLabel(null);
  }, []);
  const deferToolOutputTimeoutRefs = useRef<number[]>([]);
  const restoreMutexRef = useRef<RestoreMutex>(createRestoreMutex());
  const resetConversationKeyRef = useRef<string | undefined>(undefined);
  const toolTurnGenerationRef = useRef<number>(0);
  const clientToolsInFlightRef = useRef(0);
  const suppressStreamAbortRef = useRef(false);

  const [activeSnapshotInteractionId, setActiveSnapshotInteractionId] = useState<string | null>(
    null,
  );
  const [activeSnapshotId, setActiveSnapshotId] = useState<string | null>(null);
  const activeSnapshotInteractionIdRef = useRef<string | null>(null);
  const activeSnapshotIdRef = useRef<string | null>(null);
  const [restoreInteractionLoadingId, setRestoreInteractionLoadingId] = useState<string | null>(
    null,
  );
  const [workbenchPersistError, setWorkbenchPersistError] = useState<string | null>(null);
  const [snapshotIndexLoading, setSnapshotIndexLoading] = useState(false);
  /** One version per turn from the server (P6.4c); `unavailable` keeps the browser-list rules. */
  const turnVersionsRef = useRef<Map<string, TurnVersion>>(new Map());
  const [turnVersionsStatus, setTurnVersionsStatus] = useState<TurnVersionsStatus>('unavailable');
  /** Bumped when versions were re-read in place (another tab changed one, P6.4e). */
  const [turnVersionsRevision, setTurnVersionsRevision] = useState(0);
  const turnVersions = useMemo<TurnVersionsLookup>(
    () => ({
      status: turnVersionsStatus,
      get: (llmInteractionId: string) => turnVersionsRef.current.get(llmInteractionId),
    }),
    [turnVersionsStatus, turnVersionsRevision],
  );
  /**
   * The Chef project a turn builds in: the live value from `projectCreated`, else the one its
   * version's prototype records on the server (P6.4c). The `sessionStorage` copy is only the
   * fallback while versions are unavailable.
   */
  const chefProjectIdForTurn = (llmInteractionId: string | null | undefined): string | null =>
    chefProjectIdRef.current ??
    (llmInteractionId ? (turnVersionsRef.current.get(llmInteractionId)?.chefProjectId ?? null) : null);
  const [bootstrapRetryNonce, setBootstrapRetryNonce] = useState(0);
  /** Bumped by "Retry restore" so the snapshot index is fetched again before restoring. */
  const [snapshotIndexNonce, setSnapshotIndexNonce] = useState(0);
  const snapshotIndexRef = useRef<Map<string, import('./utils/shouldStartBuildStream').SnapshotIndexEntry>>(
    new Map(),
  );
  const toolStepsPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timelineRebuildRafRef = useRef<number | null>(null);

  const clearDeferToolOutputTimeouts = useCallback((): void => {
    for (const id of deferToolOutputTimeoutRefs.current) {
      window.clearTimeout(id);
    }
    deferToolOutputTimeoutRefs.current = [];
  }, []);

  const resetWorkbenchTurnState = useCallback((): void => {
    abortCurrentTurn();
    resumeBuildAbortRef.current?.abort();
    resumeBuildAbortRef.current = null;
    resumeBuildInFlightRef.current = null;
    resumeBuildAttemptedRef.current = new Set();
    clearDeferToolOutputTimeouts();
    cancelStreamContinuationRef.current?.();
    cancelStreamContinuationRef.current = null;
    continuationSentRef.current = false;
    toolResultContinuationPendingRef.current = false;
    buildRepeatRef.current = { lastSig: '', count: 0 };
    toolContinuationCountRef.current = 0;
    editPlannedFilePathsRef.current = [];
    truncationContinueRef.current = 0;
    streamTruncatedRef.current = false;
    processedAnnotationsRef.current.clear();
    writtenAssetPathsRef.current.clear();
    assetWriteChainRef.current = Promise.resolve();
    timelineAnnotationRef.current = null;
    buildTimelineRef.current = null;
    setBuildTimeline(null);
    setToolSteps([]);
    toolStepsRef.current = [];
    setActiveSnapshotInteractionId(null);
    setActiveSnapshotId(null);
    abortRestore(restoreMutexRef.current);
  }, [clearDeferToolOutputTimeouts, abortCurrentTurn]);

  /**
   * Never call `addToolOutput` synchronously inside `onToolCall` — the AI SDK runs
   * `onToolCall` on a SerialJobExecutor and `addToolOutput` deadlocks (never settles).
   */
  const deferAddToolOutput = useCallback((payload: AddToolOutputArgs, capturedTurn: number): void => {
    const add = addToolOutputRef.current;
    if (!add) return;
    const timeoutId = window.setTimeout(() => {
      deferToolOutputTimeoutRefs.current = deferToolOutputTimeoutRefs.current.filter(
        (id) => id !== timeoutId,
      );
      if (isTurnStale(turnSessionRef.current, capturedTurn)) {
        return;
      }
      void Promise.resolve(add(payload))
        .then(() => {
          if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
          scheduleContinueAfterClientTools();
        })
        .catch((err: unknown) => {
          console.warn('[Workbench] addToolOutput failed', err);
          setWorkbenchPersistError('Tool output could not be sent. Try Retry or refresh.');
        });
    }, 0);
    deferToolOutputTimeoutRefs.current.push(timeoutId);
  }, []);

  const getEditLoopCompletenessInput = useCallback(
    (messages: readonly unknown[]) => ({
      messages,
      toolSteps: toolStepsRef.current,
      plannedFilePaths: editPlannedFilePathsRef.current,
      toolResultContinuationPending: toolResultContinuationPendingRef.current,
      clientToolsBusy: workbenchStore.clientToolsBusy.get(),
    }),
    [],
  );

  const requestStreamContinuation = useCallback(() => {
    if (turnSessionRef.current.aborted) return;
    cancelStreamContinuationRef.current?.();
    cancelStreamContinuationRef.current = scheduleStreamContinuation({
      getMessages: () => messagesRef.current,
      getToolSteps: () => toolStepsRef.current,
      getChatStatus: () => chatStatusRef.current,
      getBuildRepeatCount: () => buildRepeatRef.current.count,
      getToolResultContinuationPending: () => toolResultContinuationPendingRef.current,
      getToolContinuationCount: () => toolContinuationCountRef.current,
      getPlannedFilePaths: () => editPlannedFilePathsRef.current,
      getTruncated: () => streamTruncatedRef.current,
      getClientToolsBusy: () => workbenchStore.clientToolsBusy.get(),
      // Re-evaluate on the events it waits for instead of only polling (FE-7).
      subscribe: (wake) => {
        continuationWakersRef.current.add(wake);
        const unlistenBusy = workbenchStore.clientToolsBusy.listen(() => wake());
        return () => {
          continuationWakersRef.current.delete(wake);
          unlistenBusy();
        };
      },
      maxBuildRepeatErrors: MAX_BUILD_REPEAT_ERRORS,
      maxToolContinuations: () =>
        computeMaxToolContinuations(
          Math.max(
            editPlannedFilePathsRef.current.length,
            pathsTouchedThisTurn(toolStepsRef.current).size,
          ),
        ),
      onStreaming: (streaming) => {
        workbenchStore.isStreaming.set(streaming);
      },
      onExhausted: (reason) => {
        workbenchStore.isStreaming.set(false);
        const base = buildTimelineRef.current ?? createEmptyTimeline();
        const finalT = markStreamEndedWithoutSuccessfulBuild(base);
        buildTimelineRef.current = finalT;
        setBuildTimeline(finalT);
      },
      send: () => {
        if (turnSessionRef.current.aborted) return;
        const budgetLid = streamingBuildLlmInteractionIdRef?.current;
        if (budgetLid && turnBudgetsRef.current.expired(budgetLid, Date.now())) {
          expireTurnBudgetRef.current(budgetLid);
          return;
        }
        if (continuationSentRef.current) return;
        continuationSentRef.current = true;
        toolContinuationCountRef.current += 1;
        toolResultContinuationPendingRef.current = false;
        window.setTimeout(() => {
          continuationSentRef.current = false;
        }, 500);
        void sendMessageRef.current?.();
      },
    });
  }, []);

  const scheduleContinueAfterClientTools = useCallback(() => {
    ++continueScheduleGenRef.current;
    requestStreamContinuation();
  }, [requestStreamContinuation]);

  const tryAutoContinueAfterFailedBuild = useCallback(
    (finishMessages: readonly unknown[]): boolean => {
      if (buildRepeatRef.current.count >= MAX_BUILD_REPEAT_ERRORS) return false;
      if (!shouldAutoContinueAfterFailedBuild(finishMessages, toolStepsRef.current)) {
        return false;
      }
      requestStreamContinuation();
      return true;
    },
    [requestStreamContinuation],
  );

  /**
   * A turn cut off at `maxOutputTokens` stops before the model emits `build`, so the
   * prototype never gets compiled. Resume it so the model can finish and call `build`.
   */
  const tryAutoContinueAfterTruncation = useCallback((): boolean => {
    if (!streamTruncatedRef.current) return false;
    if (truncationContinueRef.current >= MAX_TRUNCATION_CONTINUES) return false;
    truncationContinueRef.current += 1;
    requestStreamContinuation();
    return true;
  }, [requestStreamContinuation]);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    modelRef.current = { modelProvider, modelChoice };
  }, [modelProvider, modelChoice]);

  useEffect(() => {
    activeSnapshotInteractionIdRef.current = activeSnapshotInteractionId;
    activeSnapshotIdRef.current = activeSnapshotId;
  }, [activeSnapshotInteractionId, activeSnapshotId]);

  useEffect(() => {
    projectConversationRef.current = { projectId, conversationId };
  }, [projectId, conversationId]);

  // The card survives a reload: read which version, if any, is waiting for the founder.
  useEffect(() => {
    setVersionAttention(null);
    setAutoFixLabel(null);
    fixTurnIdRef.current = null;
    if (!projectId || !conversationId) return;
    let cancelled = false;
    prototyperService
      .listConversationPrototypes(projectId, conversationId)
      .then((prototypes) => {
        if (cancelled) return;
        setVersionAttention((current) => current ?? attentionFromPrototypes(prototypes));
      })
      .catch(() => {
        // Versions API unavailable: no card; builds work as before.
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, conversationId]);

  // Another tab changed a version, or this tab became visible again (another device may have):
  // re-read versions and the card (P6.4e).
  const [versionsRefreshNonce, setVersionsRefreshNonce] = useState(0);
  refreshTurnVersionsRef.current = () => setVersionsRefreshNonce((n) => n + 1);
  useEffect(() => {
    if (!conversationId) return undefined;
    const unsubscribe = versionEventsRef.current?.subscribe(conversationId, () =>
      setVersionsRefreshNonce((n) => n + 1),
    );
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') setVersionsRefreshNonce((n) => n + 1);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      unsubscribe?.();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [conversationId]);

  useEffect(() => {
    if (versionsRefreshNonce === 0 || !projectId || !conversationId) return undefined;
    let cancelled = false;
    prototyperService
      .listConversationTurnVersions(projectId, conversationId)
      .then((versions) => {
        if (cancelled) return;
        turnVersionsRef.current = indexTurnVersions(versions);
        setTurnVersionsStatus('ready');
        setTurnVersionsRevision((n) => n + 1);
      })
      .catch(() => {});
    prototyperService
      .listConversationPrototypes(projectId, conversationId)
      .then((prototypes) => {
        if (cancelled) return;
        // The server's card wins; a local "no credits" notice stays until something replaces it.
        setVersionAttention((current) => attentionFromPrototypes(prototypes) ?? (current?.notice ? current : null));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [versionsRefreshNonce, projectId, conversationId]);

  // The "saved in another tab" banner's choices (P6.4e).
  useEffect(() => {
    const loadLatest = (): void => {
      const conflict = workbenchStore.saveConflict.get();
      if (!conflict) return;
      workbenchStore.saveConflict.set(null);
      // The turn is loaded, but its newest snapshot is another tab's: restore it anyway.
      activeSnapshotIdRef.current = null;
      void restoreSnapshotRef.current(conflict.llmInteractionId).catch((err) => {
        console.warn('[Workbench] loading the latest saved version failed', err);
      });
    };
    const keepMine = (): void => {
      if (!workbenchStore.saveConflict.get()) return;
      workbenchStore.saveConflict.set(null);
      void runAutoSnapshotRef.current('files', { force: true, overwrite: true });
    };
    window.addEventListener(WORKBENCH_SAVE_CONFLICT_LOAD_LATEST_EVENT, loadLatest);
    window.addEventListener(WORKBENCH_SAVE_CONFLICT_KEEP_MINE_EVENT, keepMine);
    return () => {
      window.removeEventListener(WORKBENCH_SAVE_CONFLICT_LOAD_LATEST_EVENT, loadLatest);
      window.removeEventListener(WORKBENCH_SAVE_CONFLICT_KEEP_MINE_EVENT, keepMine);
    };
  }, []);

  // "Undo this change" needs an earlier verified version of the same prototype.
  const attentionVersionId = versionAttention?.versionId ?? null;
  useEffect(() => {
    setVersionAttentionCanUndo(false);
    if (!attentionVersionId || !projectId || !conversationId) return;
    let cancelled = false;
    prototyperService
      .listConversationPrototypes(projectId, conversationId)
      .then((prototypes) => {
        if (cancelled) return;
        const prototype = prototypes.find((p) => p.latestVersion?.id === attentionVersionId);
        setVersionAttentionCanUndo(
          Boolean(prototype?.currentVersionId && prototype.currentVersionId !== attentionVersionId)
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [attentionVersionId, projectId, conversationId]);

  const patchBuildSessionApi = useCallback(
    async (body: Record<string, unknown>) => {
      const id = streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId;
      if (!id) return;
      try {
        await prototyperService.patchPrototyperBuildSession(id, body);
      } catch (e) {
        console.warn('[Workbench] prototyper build session PATCH failed', e);
      }
    },
    [streamingLlmInteractionId],
  );

  const recordSnapshotFingerprintForInteraction = useCallback(
    (llmInteractionId: string, buildVerified: boolean): void => {
      recordNewestSnapshotFingerprint(
        lastSnapshotFingerprintByInteractionRef.current,
        llmInteractionId,
        buildVerified,
        prototypeSnapshotContentFingerprint(),
      );
    },
    [],
  );

  const runAutoPrototypeSnapshot = useCallback(
    async (
      reason: AutoSnapshotReason,
      options?: {
        buildVerified?: boolean;
        retry?: boolean;
        /** Bypass the streaming/busy + cooldown gates. Build-phase callers only. */
        force?: boolean;
        /** Explicit attribution for turns whose `activeSnapshotInteractionId` isn't set yet. */
        llmInteractionId?: string;
        /** "Keep my copy" after a stale-save refusal (P6.4e). */
        overwrite?: boolean;
      },
    ) => {
      const { projectId: pid, conversationId: cid } = projectConversationRef.current;
      if (!cid) return;
      if (!pid) {
        return;
      }
      const force = options?.force === true;
      if (typeof window === 'undefined' || !window.crossOriginIsolated) return;
      if (workbenchStore.bootStatus.get() !== 'ready') return;
      // The starter page is never a version worth keeping, and saving it replaces one that is.
      if (workbenchStore.isStarterWorkspace()) return;
      if (workbenchStore.restoreInProgress.get()) {
        // `applySnapshotZipToWebContainer` holds this flag for ~4.5s *after* its writes land.
        // Hard-returning here would silently drop the verified upload of a fast resumed build,
        // so a forced caller waits it out instead of giving up.
        if (!force) return;
        const waitUntil = Date.now() + SNAPSHOT_RESTORE_WAIT_MS;
        while (workbenchStore.restoreInProgress.get() && Date.now() < waitUntil) {
          await new Promise((resolve) => window.setTimeout(resolve, 150));
        }
      }

      if (
        shouldDeferForBusy({
          force,
          isStreaming: workbenchStore.isStreaming.get(),
          clientToolsBusy: workbenchStore.clientToolsBusy.get(),
        })
      ) {
        if (reason === 'files') {
          pendingFileSnapshotAfterBusyRef.current = true;
        }
        return;
      }

      const streamingTurnId =
        streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId ?? null;
      const activeTurnId = activeSnapshotInteractionId;
      const attributionId =
        options?.llmInteractionId ??
        (reason === 'files' ? activeTurnId : streamingTurnId ?? activeTurnId);

      if (!attributionId) return;

      if (
        reason === 'files' &&
        streamingTurnId &&
        activeTurnId &&
        streamingTurnId !== activeTurnId
      ) {
        return;
      }

      if (autoSnapshotInFlightRef.current) {
        if (!force) {
          if (reason === 'files') {
            pendingFileSnapshotAfterBusyRef.current = true;
          }
          return;
        }
        // A forced call (build-phase) must not be dropped just because another upload is
        // mid-flight — e.g. the pre-build snapshot may still be uploading when the build
        // finishes. Queue behind it instead, bounded so a wedged upload can't wedge this one.
        await Promise.race([
          autoSnapshotChainRef.current.catch(() => undefined),
          new Promise((resolve) => window.setTimeout(resolve, SNAPSHOT_CHAIN_MAX_WAIT_MS)),
        ]);
      }

      const now = Date.now();
      const sinceFile = now - lastFileSnapshotAtRef.current;
      const sinceBuildStream = now - lastBuildStreamSnapshotAtRef.current;

      if (
        shouldSkipForCooldown({
          reason,
          force,
          sinceFileMs: sinceFile,
          sinceBuildStreamMs: sinceBuildStream,
          fileCooldownMs: AUTO_SNAPSHOT_COOLDOWN_FILES_MS,
          buildStreamCooldownMs: AUTO_SNAPSHOT_COOLDOWN_BUILD_STREAM_MS,
        })
      ) {
        if (reason === 'files') {
          const wait = AUTO_SNAPSHOT_COOLDOWN_FILES_MS - sinceFile + 200;
          if (fileSnapshotCooldownRetryTimerRef.current !== undefined) {
            clearTimeout(fileSnapshotCooldownRetryTimerRef.current);
          }
          fileSnapshotCooldownRetryTimerRef.current = setTimeout(() => {
            fileSnapshotCooldownRetryTimerRef.current = undefined;
            void runAutoPrototypeSnapshot('files');
          }, wait);
        }
        return;
      }

      const wantVerified = options?.buildVerified === true;
      const fingerprint = prototypeSnapshotContentFingerprint();
      const priorFingerprint = lastSnapshotFingerprintByInteractionRef.current.get(
        snapshotDedupKey(attributionId, wantVerified),
      );
      if (priorFingerprint === fingerprint) {
        if (reason === 'stream-end') {
          onBuildStreamTurnCompleted?.(attributionId);
        }
        // Content hasn't changed since the last upload for this exact (interaction,
        // buildVerified) pair — skip re-uploading, but still hand the caller the snapshot
        // that's already on the server instead of `undefined`. `uploadVerifiedPrototypeSnapshot`
        // otherwise has to fall back to `activeSnapshotId`, which is empty the first time
        // publish is invoked in a session that restored via bootstrap (no live upload ever
        // ran), even though a verified snapshot already exists at this fingerprint.
        const existing = snapshotIndexRef.current.get(attributionId);
        if (wantVerified) {
          // Same files as the verified snapshot already stored: still verify the version.
          const sameContentCtx = versionTurnContext(attributionId);
          if (sameContentCtx) {
            void versionTrackerRef.current
              ?.verified(sameContentCtx, {
                contentHash: fingerprint,
                snapshotId: existing?.snapshotId,
                chefProjectId: chefProjectIdRef.current,
              })
              .then((version) => recordVersionNumber(attributionId, version));
          }
        }
        if (existing) {
          return { snapshotId: existing.snapshotId, revision: existing.revision, status: 'ready' };
        }
        return undefined;
      }

      autoSnapshotInFlightRef.current = true;
      const uploadPromise = (async () => {
        try {
          const lid = attributionId;
          const blob = await buildPrototypeSnapshotZipBlob();
          let finalized: Awaited<ReturnType<typeof prototyperService.uploadPrototypeSnapshotZip>>;
          try {
            finalized = await prototyperService.uploadPrototypeSnapshotZip(
              pid,
              cid,
              blob,
              lid,
              options?.buildVerified,
              // Manual edits carry the revision they started from, so a save cannot silently
              // replace what another tab saved in between (P6.4e).
              reason === 'files'
                ? {
                    baseRevision: snapshotIndexRef.current.get(lid)?.revision,
                    overwrite: options?.overwrite === true,
                  }
                : undefined,
            );
          } catch (err) {
            if (err instanceof Error && err.name === 'PrototypeSnapshotStaleError') {
              workbenchStore.saveConflict.set({ llmInteractionId: lid });
              return undefined;
            }
            throw err;
          }
          const at = Date.now();
          if (reason === 'files') {
            lastFileSnapshotAtRef.current = at;
          } else {
            lastBuildStreamSnapshotAtRef.current = at;
          }
          if (finalized) {
            workbenchStore.saveConflict.set(null);
            recordSnapshotFingerprintForInteraction(lid, wantVerified);
            setActiveSnapshotId(finalized.snapshotId);
            if (reason === 'files' && !workbenchStore.isStreaming.get()) {
              // The founder saved an edit. The version's snapshot has it; the builder's copy,
              // which the next edit turn reads and patches, does not. After a page reload the
              // browser no longer knows which files it edited and sends none with that turn, so
              // the model rewrote the file from the stale copy and the edit was lost. The
              // backend accepts only the prototype's newest version; anything else is skipped.
              void saveFounderEditsToBuilderRef.current(pid, cid, lid);
            }
            if (wantVerified) {
              const verifiedCtx = versionTurnContext(lid);
              if (verifiedCtx) {
                void versionTrackerRef.current
                  ?.verified(verifiedCtx, {
                    contentHash: fingerprint,
                    snapshotId: finalized.snapshotId,
                    chefProjectId: chefProjectIdRef.current,
                  })
                  .then((version) => recordVersionNumber(lid, version));
              }
            }
            const prevEntry = snapshotIndexRef.current.get(lid);
            snapshotIndexRef.current.set(lid, {
              // Keep what the server index said about the turn, above all its demo link id: the
              // Publish menu reads it to know the version's prototype is published. Dropped here,
              // any version saved again this session (opening one saves it) showed as unpublished
              // and offered a second link.
              ...prevEntry,
              llmInteractionId: lid,
              snapshotId: finalized.snapshotId,
              revision: finalized.revision,
              createdAt: new Date().toISOString(),
              ...(wantVerified || prevEntry?.buildVerified ? { buildVerified: true } : {}),
            });
            if (lid === streamingTurnId) {
              await patchBuildSessionApi({
                prototypeSnapshotId: finalized.snapshotId,
              });
            }
            if (reason === 'stream-end') {
              onBuildStreamTurnCompleted?.(lid);
            }
          }
          setWorkbenchPersistError(null);
          return finalized;
        } catch (e) {
          console.warn('[Workbench] Auto prototype snapshot failed', { reason, err: e });
          if (reason !== 'files' && !options?.retry) {
            setTimeout(() => {
              void runAutoPrototypeSnapshot(reason, { ...options, retry: true });
            }, 2000);
          } else {
            setWorkbenchPersistError('Could not save prototype snapshot. Changes may not persist.');
          }
          return undefined;
        } finally {
          autoSnapshotInFlightRef.current = false;
          if (pendingFileSnapshotAfterBusyRef.current) {
            pendingFileSnapshotAfterBusyRef.current = false;
            queueMicrotask(() => {
              void runAutoPrototypeSnapshot('files');
            });
          }
        }
      })();
      autoSnapshotChainRef.current = uploadPromise;
      return uploadPromise;
    },
    [
      patchBuildSessionApi,
      streamingLlmInteractionId,
      activeSnapshotInteractionId,
      onBuildStreamTurnCompleted,
      recordSnapshotFingerprintForInteraction,
    ],
  );
  runAutoSnapshotRef.current = runAutoPrototypeSnapshot;

  /**
   * Writes the founder's saved edits into the builder's copy (`utils/builderSave.ts`) and shows
   * the outcome in the workbench; a failure keeps a Retry on screen.
   */
  const lastBuilderSaveRef = useRef<{ pid: string; cid: string; lid: string } | null>(null);
  const saveFounderEditsToBuilderRef = useRef(
    async (pid: string, cid: string, lid: string): Promise<void> => {
      const edits = collectFounderEditsForBuilder();
      if (Object.keys(edits).length === 0) return;
      lastBuilderSaveRef.current = { pid, cid, lid };
      workbenchStore.builderSave.set({ status: 'saving', llmInteractionId: lid });
      const outcome = await saveEditsToBuilder({
        getVersionId: () => turnVersionsRef.current.get(lid)?.id,
        save: (versionId) => prototyperService.savePrototypeVersionFiles(pid, cid, versionId, edits),
      });
      // A later save of another turn owns the status now.
      if (workbenchStore.builderSave.get()?.llmInteractionId !== lid) return;
      if (outcome === 'failed') {
        console.warn('[Workbench] saving edits to the builder failed', { llmInteractionId: lid });
      }
      workbenchStore.builderSave.set(
        outcome === 'skipped' ? null : { status: outcome, llmInteractionId: lid }
      );
    }
  );

  useEffect(() => {
    const retry = (): void => {
      const last = lastBuilderSaveRef.current;
      if (!last || workbenchStore.isStreaming.get()) return;
      void saveFounderEditsToBuilderRef.current(last.pid, last.cid, last.lid);
    };
    window.addEventListener(WORKBENCH_BUILDER_SAVE_RETRY_EVENT, retry);
    return () => window.removeEventListener(WORKBENCH_BUILDER_SAVE_RETRY_EVENT, retry);
  }, []);

  useEffect(() => {
    if (!projectId || !conversationId) return undefined;

    const clearDebounce = (): void => {
      if (fileSnapshotDebounceRef.current !== undefined) {
        clearTimeout(fileSnapshotDebounceRef.current);
        fileSnapshotDebounceRef.current = undefined;
      }
    };

    const unsub = workbenchStore.files.subscribe(() => {
      if (workbenchStore.bootStatus.get() !== 'ready') return;
      if (workbenchStore.restoreInProgress.get()) return;
      clearDebounce();
      fileSnapshotDebounceRef.current = window.setTimeout(() => {
        fileSnapshotDebounceRef.current = undefined;
        void runAutoPrototypeSnapshot('files');
      }, AUTO_SNAPSHOT_FILE_DEBOUNCE_MS);
    });

    return () => {
      unsub();
      clearDebounce();
      if (fileSnapshotCooldownRetryTimerRef.current !== undefined) {
        clearTimeout(fileSnapshotCooldownRetryTimerRef.current);
        fileSnapshotCooldownRetryTimerRef.current = undefined;
      }
    };
  }, [projectId, conversationId, runAutoPrototypeSnapshot]);

  useEffect(() => {
    const conversationKey = `${conversationId ?? ''}:${tmpConversationId ?? ''}`;
    if (resetConversationKeyRef.current === undefined) {
      resetConversationKeyRef.current = conversationKey;
      return;
    }
    if (resetConversationKeyRef.current === conversationKey) return;
    resetConversationKeyRef.current = conversationKey;
    setWarmBootLatch(0);
    resetWorkbenchTurnState();
    abortRestore(restoreMutexRef.current);
    snapshotIndexRef.current = new Map();
    setSnapshotIndexLoading(false);
    // Must run before the WebContainer boot/link effect below (declared later, so it
    // cleans up + re-runs after this effect within the same commit): that effect's
    // `syncWorkbenchStoreWithWebContainerPersist()` blindly re-applies the last known
    // build status / preview URL on every remount, including one caused by switching
    // to a different conversation. Clearing the cache here — before that resync can
    // read it — keeps a finished chat's "build succeeded" state from flashing onto a
    // different, never-built chat right after the switch.
    resetPersistedBuildCache();
    if (lightPreviewRuntimeRef.current) {
      // The compiled document belongs to the conversation being left. Nothing replaces it until
      // this one restores or generates, so it would otherwise sit there as the new
      // conversation's prototype for a second. (The WebContainer's URL is left alone: it is the
      // dev server's, and the boot effect re-applies it.)
      workbenchStore.previewUrl.set('');
    }
    workbenchStore.previewReady.set(false);
    lastSnapshotFingerprintByInteractionRef.current = new Map();
    bootstrapAttemptedKeyRef.current = undefined;
  }, [conversationId, tmpConversationId, resetWorkbenchTurnState]);

  useEffect(() => {
    // Only relevant once the workbench is actually going to be used — the index it builds
    // (`snapshotIndexRef`) is consumed solely by `shouldStartBuildStream`, which only ever
    // runs once a prototyper build-stream might start. Without this gate, opening *any*
    // conversation — including a pure Clarify one that has never touched Prototyper — fires
    // `GET .../prototype/snapshots` for it, purely wasted network noise for the common case.
    if (!projectId || !conversationId || !workbenchWanted) {
      setSnapshotIndexLoading(false);
      turnVersionsRef.current = new Map();
      setTurnVersionsStatus('unavailable');
      return;
    }
    let cancelled = false;
    const abortController = new AbortController();
    const timeoutId = window.setTimeout(() => {
      abortController.abort();
    }, 4_000);
    setSnapshotIndexLoading(true);
    // The versions load beside the index. A slow or failing versions API must never hold the
    // auto-start back: after the same 4 s the browser-list rules decide.
    setTurnVersionsStatus('loading');
    let turnVersionsSettled = false;
    const settleTurnVersions = (versions: Map<string, TurnVersion> | null): void => {
      if (cancelled || turnVersionsSettled) return;
      turnVersionsSettled = true;
      turnVersionsRef.current = versions ?? new Map();
      setTurnVersionsStatus(versions ? 'ready' : 'unavailable');
    };
    const turnVersionsTimeoutId = window.setTimeout(() => settleTurnVersions(null), 4_000);
    prototyperService
      .listConversationTurnVersions(projectId, conversationId)
      .then((versions) => settleTurnVersions(indexTurnVersions(versions)))
      .catch(() => settleTurnVersions(null))
      .finally(() => window.clearTimeout(turnVersionsTimeoutId));
    void (async () => {
      try {
        const getIndex = prototyperService.getPrototypeSnapshotIndex?.bind(prototyperService);
        if (typeof getIndex !== 'function') {
          throw new Error('getPrototypeSnapshotIndex unavailable');
        }
        const entries = await getIndex(projectId, conversationId);
        if (cancelled || abortController.signal.aborted) return;
        snapshotIndexRef.current = new Map(
          entries.map((e) => [e.llmInteractionId, e]),
        );
      } catch {
        if (!cancelled) snapshotIndexRef.current = new Map();
      } finally {
        window.clearTimeout(timeoutId);
        if (!cancelled) setSnapshotIndexLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      abortController.abort();
      window.clearTimeout(timeoutId);
      window.clearTimeout(turnVersionsTimeoutId);
    };
  }, [projectId, conversationId, workbenchWanted, snapshotIndexNonce]);

  /**
   * After refresh: restore the most recent verified snapshot into the WebContainer.
   * ConversationPageTemplate must not auto-call build-stream when bootstrap === 'restored'.
   *
   * "Latest" is derived from the snapshot index (already fetched above, whenever the
   * workbench boots) rather than a dedicated `/prototype/snapshot/latest` call — the index
   * already carries `createdAt` + `buildVerified` per interaction, and `getPrototypeSnapshotByInteraction`
   * (already used by the explicit generation-pill restore) returns the same shape the actual
   * download needs. This also means a brand-new conversation with an empty index — the common
   * case when a new chat enters Prototyper mode — skips straight to 'none' with zero network
   * calls, instead of firing a request that's guaranteed to 404.
   */
  useEffect(() => {
    if (!projectId || !conversationId) {
      setSnapshotBootstrap('none');
      return;
    }
    if (typeof window === 'undefined' || !window.crossOriginIsolated) {
      setSnapshotBootstrap('none');
      return;
    }
    if (bootStatus === 'error') {
      setSnapshotBootstrap('none');
      return;
    }
    if (bootStatus !== 'ready' || snapshotIndexLoading || turnVersions.status === 'loading') {
      setSnapshotBootstrap('pending');
      return;
    }

    const indexEntries = Array.from(snapshotIndexRef.current.values());
    if (indexEntries.length === 0) {
      setSnapshotBootstrap('none');
      return;
    }
    // Newest verified; never a discarded or superseded turn once versions answer (P6.4c).
    // A `?restoreInteraction=` from the artifacts library names the version to open, so it wins
    // over that choice — but only when the conversation actually has that turn's snapshot.
    const preferredEntry = preferredRestoreInteractionId
      ? indexEntries.find((e) => e.llmInteractionId === preferredRestoreInteractionId)
      : undefined;
    const latestEntry = preferredEntry ?? pickBootstrapEntry(indexEntries, turnVersions);
    if (!latestEntry) {
      setSnapshotBootstrap('none');
      return;
    }

    const bootstrapKey = `${conversationId}:${bootstrapRetryNonce}`;
    // Order matters, and it used to be wrong. This effect re-runs whenever `bootStatus` dips out
    // of 'ready' (which `workbenchStore.reset()` does on every boot-effect cleanup) or
    // `snapshotIndexLoading` flips true again — and each of those re-runs writes 'pending' at the
    // `bootStatus !== 'ready'` guard above. So every early-return below has to leave the state
    // machine somewhere terminal; a bare `return` re-pins 'pending' forever, which keeps
    // `workbenchPreparing` (ConversationPage) true and unmounts the whole workbench subtree —
    // including every error/retry affordance that could rescue it.
    if (activeSnapshotInteractionIdRef.current) {
      // A snapshot is already applied to the container. Must be checked *before* the key guard:
      // once `bootstrapAttemptedKeyRef` matches, the old ordering made this branch unreachable,
      // so a restored session that later saw a `bootStatus` dip could never get back out of
      // 'pending'.
      setSnapshotBootstrap('restored');
      return;
    }
    if (restoreMutexRef.current.abortController !== null) {
      // A restore genuinely owns the outcome right now (this bootstrap's, or an explicit
      // generation-pill click that superseded it) — 'pending' is the honest state and that
      // owner settles it.
      return;
    }
    if (bootstrapAttemptedKeyRef.current === bootstrapKey) {
      // Already attempted for this conversation+nonce, nothing in flight, and no snapshot
      // landed — the attempt is over, so don't leave 'pending' pinned. Preserve a terminal
      // value a previous run already reached ('failed' keeps the retry UI reachable).
      setSnapshotBootstrap((prev) => (prev === 'pending' ? 'none' : prev));
      return;
    }

    bootstrapAttemptedKeyRef.current = bootstrapKey;
    setSnapshotRestoreError(null);
    setSnapshotBootstrap('pending');

    /**
     * Routed through the same `RestoreMutex` as `restorePrototypeSnapshotForInteraction` so
     * this automatic restore and an explicit generation-pill click can't race each other —
     * whichever starts second aborts the other via `signal`, and `applySnapshotZipToWebContainer`
     * genuinely honors that signal mid-write (unlike the plain `cancelled` boolean this replaced,
     * which only gated whether the *result* got applied, not whether the writes themselves kept
     * running after being superseded).
     */
    void withRestoreMutex(restoreMutexRef.current, `bootstrap:${conversationId}`, async (signal) => {
      try {
        let latestMeta: {
          llmInteractionId?: string | null;
          buildVerified?: boolean;
          snapshotId?: string;
        } = {};
        /**
         * Download one turn's snapshot. A 403/404 from storage gets one freshly signed URL
         * before it counts as missing: the first URL may have expired, and finalizing a newer
         * snapshot of the same turn deletes the object behind an older one.
         */
        const downloadVersion = async (llmInteractionId: string) => {
          for (let attempt = 0; attempt < 2; attempt += 1) {
            const meta = await prototyperService.getPrototypeSnapshotByInteraction(
              projectId,
              conversationId,
              llmInteractionId,
            );
            if (signal.aborted) {
              throw new DOMException('Snapshot restore aborted', 'AbortError');
            }
            // A version opened before in any tab of this browser comes from the local cache.
            const cached = await readCachedSnapshotZip(snapshotZipCacheKey(meta));
            if (cached) {
              return { meta, blob: cached };
            }
            const res = await fetch(meta.downloadUrl, { signal });
            if (res.ok) {
              const downloaded = await res.blob();
              if (downloaded.size === 0) {
                throw new Error('Empty snapshot');
              }
              void writeCachedSnapshotZip(snapshotZipCacheKey(meta), downloaded);
              return { meta, blob: downloaded };
            }
            if (res.status !== 403 && res.status !== 404) {
              throw new Error(`Snapshot download failed: ${res.status}`);
            }
          }
          throw new SnapshotObjectMissingError(llmInteractionId);
        };

        let usedFallbackVersion = false;
        const blob = await wcPerfAsync('wc:snapshot:downloadZip', async () => {
          let chosen: Awaited<ReturnType<typeof downloadVersion>> | undefined;
          try {
            chosen = await downloadVersion(latestEntry.llmInteractionId);
          } catch (primaryError) {
            if (!isSnapshotObjectMissingError(primaryError)) {
              throw primaryError;
            }
            // The newest version's file is gone. Restore the next-best saved version
            // (verified first, then newest) instead of stranding the founder on an error.
            const candidates = restorableEntries(indexEntries, turnVersions)
              .filter((e) => e.llmInteractionId !== latestEntry.llmInteractionId)
              .sort((a, b) => Number(b.buildVerified === true) - Number(a.buildVerified === true));
            for (const candidate of candidates) {
              try {
                chosen = await downloadVersion(candidate.llmInteractionId);
                usedFallbackVersion = true;
                break;
              } catch (candidateError) {
                if (
                  !isSnapshotObjectMissingError(candidateError) &&
                  !isBenignMissingSnapshotError(candidateError)
                ) {
                  throw candidateError;
                }
              }
            }
            if (!chosen) {
              throw primaryError;
            }
          }
          latestMeta = {
            llmInteractionId: chosen.meta.llmInteractionId,
            buildVerified: chosen.meta.buildVerified,
            snapshotId: chosen.meta.snapshotId,
          };
          return chosen.blob;
        });
        if (signal.aborted) {
          throw new DOMException('Snapshot restore aborted', 'AbortError');
        }
        if (!(await applySnapshotBlobToRuntime(blob, signal))) {
          setSnapshotBootstrap('none');
          return;
        }
        markSnapshotPreviewReady({
          buildVerified: latestMeta.buildVerified === true,
          knownBuildSuccess: latestMeta.buildVerified === true,
        });
        if (latestMeta.llmInteractionId) {
          setActiveSnapshotInteractionId(latestMeta.llmInteractionId);
          recordSnapshotFingerprintForInteraction(
            latestMeta.llmInteractionId,
            latestMeta.buildVerified === true,
          );
        }
        if (latestMeta.snapshotId) {
          setActiveSnapshotId(latestMeta.snapshotId);
        }
        setSnapshotRestoreNotice(
          usedFallbackVersion
            ? "Your latest prototype version couldn't be loaded, so the previous saved version is shown."
            : null,
        );
        setSnapshotBootstrap('restored');
        onSnapshotRestored?.(latestMeta);
      } catch (e) {
        // Superseded by a newer bootstrap restore or an explicit generation-pill click —
        // that call owns the outcome now; applying anything here would be a stale write.
        if (signal.aborted) return;
        if (isBenignMissingSnapshotError(e)) {
          setSnapshotRestoreError(null);
          setSnapshotBootstrap('none');
          if (lightPreviewRuntimeRef.current) {
            // The store layered over the template is the whole workspace here, so emptying it
            // shows the template again — the light preview's `resetContainerToTemplate`.
            resetPreviewStoreToTemplate();
            recordProductionBuildOutcome(false);
          } else {
            const wc = webcontainerRef.current ?? getWebContainer();
            if (wc) {
              await waitForTemplateReady();
              await resetContainerToTemplate(wc);
              // The tab-level "last build succeeded" cache would otherwise mark the bare
              // template as a finished build on the next remount and show it (SN-4).
              recordProductionBuildOutcome(false);
              restartDevServerAfterSnapshot(wc);
            }
          }
          setActiveSnapshotInteractionId(null);
          setActiveSnapshotId(null);
          return;
        }
        const message = isSnapshotObjectMissingError(e)
          ? 'the saved prototype file is missing and no earlier version could be loaded'
          : e instanceof Error
            ? e.message
            : 'Failed to restore prototype snapshot';
        console.warn('[Workbench] Snapshot restore failed', e);
        setSnapshotRestoreError(message);
        setSnapshotBootstrap('failed');
      }
    });
  }, [
    turnVersions,
    bootStatus,
    projectId,
    conversationId,
    onSnapshotRestored,
    bootstrapRetryNonce,
    snapshotIndexLoading,
    recordSnapshotFingerprintForInteraction,
    preferredRestoreInteractionId,
  ]);

  /** Flush pending file-edit snapshot on tab hide / unload (best-effort). */
  useEffect(() => {
    if (!projectId || !conversationId) return undefined;

    const flushPendingSnapshot = (): void => {
      if (fileSnapshotDebounceRef.current !== undefined) {
        clearTimeout(fileSnapshotDebounceRef.current);
        fileSnapshotDebounceRef.current = undefined;
      }
      void runAutoPrototypeSnapshot('files');
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        flushPendingSnapshot();
      }
    };

    window.addEventListener('pagehide', flushPendingSnapshot);
    window.addEventListener('beforeunload', flushPendingSnapshot);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', flushPendingSnapshot);
      window.removeEventListener('beforeunload', flushPendingSnapshot);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [projectId, conversationId, runAutoPrototypeSnapshot]);

  /**
   * Chrome (and other browsers) freeze/throttle a long-backgrounded tab's timers, network, and
   * nested workers — the WebContainer's dev server runs inside exactly such a worker, so a long
   * time away can leave it genuinely unresponsive with no error until the user notices the dead
   * preview and clicks Restart. This proactively re-checks health on tab *return* and recovers
   * without waiting for that manual step. Scoped to `devServerStatus` only — a stuck `bootStatus`
   * already self-resolves via `WEBCONTAINER_BOOT_TIMEOUT_MS` regardless of tab visibility.
   */
  useEffect(() => {
    if (!projectId || !conversationId) return undefined;

    const HIDDEN_RECOVERY_THRESHOLD_MS = 60_000;
    const hiddenAtRef = { current: null as number | null };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt == null || Date.now() - hiddenAt < HIDDEN_RECOVERY_THRESHOLD_MS) return;

      const status = workbenchStore.devServerStatus.get();
      if (status !== 'crashed' && status !== 'error') return;
      const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
      if (!wc) return;
      restartDevServerAfterSnapshot(wc);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [projectId, conversationId]);

  /** Cross-origin isolation is required independently of deferred boot timing. */
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.crossOriginIsolated) {
      workbenchStore.bootStatus.set('error');
      workbenchStore.bootError.set(
        'Cross-origin isolation is required. Use a browser with COOP/COEP headers enabled.',
      );
    }
  }, []);

  /** WebContainer lifecycle: optionally deferred until Nest-scoped snapshots or prototype UI loads. */
  useEffect(() => {
    if (typeof window === 'undefined' || !window.crossOriginIsolated) return;

    let mounted = true;

    const linkSingletonIntoStoreIfAlive = (): boolean => {
      if (!mounted) return false;
      const wc = getWebContainer();
      if (!wc) return false;
      syncWorkbenchStoreWithWebContainerPersist();
      webcontainerRef.current = wc;
      workbenchStore.webcontainer.set(wc);
      workbenchStore.bootStatus.set('ready');
      workbenchStore.bootError.set(undefined);
      return true;
    };

    if (!shouldBootWebContainer) {
      // The light preview owns the panel: a WebContainer left over from an earlier boot in this
      // tab must stay out of the store. Linking it would publish its dev-server URL and boot
      // status over the compiled document, and hand the restore paths a container to write to.
      if (lightPreviewRuntimeRef.current) {
        return () => {
          mounted = false;
        };
      }
      if (!linkSingletonIntoStoreIfAlive()) {
        workbenchStore.bootStatus.set('idle');
        workbenchStore.bootError.set(undefined);
      }
      return () => {
        mounted = false;
        webcontainerRef.current = null;
        workbenchStore.reset();
      };
    }

    workbenchStore.bootStatus.set('booting');
    workbenchStore.bootError.set(undefined);

    if (linkSingletonIntoStoreIfAlive()) {
      return () => {
        mounted = false;
        webcontainerRef.current = null;
        workbenchStore.reset();
      };
    }

    void bootWebContainer()
      .then((wc) => {
        if (!mounted) return;
        syncWorkbenchStoreWithWebContainerPersist();
        webcontainerRef.current = wc;
        workbenchStore.webcontainer.set(wc);
        workbenchStore.bootStatus.set('ready');
        workbenchStore.bootError.set(undefined);
      })
      .catch((err: unknown) => {
        console.error('[Workbench] WebContainer boot failed:', err);
        if (!mounted) return;
        workbenchStore.bootStatus.set('error');
        let msg = err instanceof Error ? err.message : 'WebContainer failed to start';
        if (/single WebContainer|Only a single/i.test(msg)) {
          msg =
            'Only one WebContainer instance is allowed per tab. Refresh this page (F5), especially after a hot reload, or close other tabs running the workbench.';
        }
        workbenchStore.bootError.set(msg);
      });

    return () => {
      mounted = false;
      webcontainerRef.current = null;
      workbenchStore.reset();
      // Do not teardown WebContainer here: React Strict Mode remounts before async
      // teardown finishes and @webcontainer/api allows only one boot per page.
    };
  }, [shouldBootWebContainer]);

  /**
   * Tells the panel not to read the starter page as "no saved version" while the tab is still
   * finding out whether this conversation has one: the snapshot index request, the zip download,
   * the writes, and an explicit version restore.
   *
   * Keyed by conversation, because `snapshotBootstrap` is not reset when the founder switches to
   * another one. Opening a second prototyper conversation therefore inherited the first's
   * `restored`, the flag read false for the whole of the new conversation's restore, and the
   * empty state flashed exactly where this was meant to stop it.
   */
  const bootstrapSettledKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const conversationKey = `${conversationId ?? ''}:${tmpConversationId ?? ''}`;
    if (snapshotBootstrap !== 'pending') {
      bootstrapSettledKeyRef.current = conversationKey;
    }
    const decided =
      bootstrapSettledKeyRef.current === conversationKey &&
      !snapshotIndexLoading &&
      restoreInteractionLoadingId == null;
    workbenchStore.snapshotRestorePending.set(workbenchWanted && !decided);
  }, [
    workbenchWanted,
    snapshotIndexLoading,
    snapshotBootstrap,
    restoreInteractionLoadingId,
    conversationId,
    tmpConversationId,
  ]);

  const apiUrl =
    projectId && conversationId
      ? ApiUrls.projectConversationBuildStream(projectId, conversationId)
      : '';

  /**
   * Must stay stable while `apiUrl` switches tmp → project after `projectCreated`.
   * `useChat` recreates the Chat when `id` changes; a new instance has empty `messages`, so
   * `addToolOutput` (bound via ref to the new chat) runs against [] and throws on `.parts`.
   */
  const workbenchChatId = useMemo(() => {
    const turnId =
      streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId ?? 'pending';
    return `workbench-build:${conversationId ?? 'pending'}:${turnId}`;
  }, [conversationId, streamingLlmInteractionId]);

  useEffect(() => {
    chefProjectIdRef.current = null;
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      setToolSteps([]);
      return;
    }
    try {
      const raw = sessionStorage.getItem(workbenchBrowserPersistKey(workbenchChatId));
      if (!raw) {
        setToolSteps([]);
        return;
      }
      const data = JSON.parse(raw) as {
        toolSteps?: ToolStep[];
        chefProjectId?: string | null;
      };
      if (Array.isArray(data.toolSteps)) {
        setToolSteps(data.toolSteps);
      } else {
        setToolSteps([]);
      }
      if (typeof data.chefProjectId === 'string' && data.chefProjectId.trim()) {
        chefProjectIdRef.current = data.chefProjectId.trim();
      }
    } catch {
      setToolSteps([]);
    }
  }, [workbenchChatId]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return;
    }
    if (toolStepsPersistTimerRef.current !== null) {
      clearTimeout(toolStepsPersistTimerRef.current);
    }
    toolStepsPersistTimerRef.current = setTimeout(() => {
      toolStepsPersistTimerRef.current = null;
      try {
        sessionStorage.setItem(
          workbenchBrowserPersistKey(workbenchChatId),
          JSON.stringify({
            v: 1,
            toolSteps,
            chefProjectId: chefProjectIdRef.current,
          }),
        );
      } catch {
        /* ignore quota */
      }
    }, TOOL_STEPS_PERSIST_DEBOUNCE_MS);
    return () => {
      if (toolStepsPersistTimerRef.current !== null) {
        clearTimeout(toolStepsPersistTimerRef.current);
        toolStepsPersistTimerRef.current = null;
      }
    };
  }, [toolSteps, workbenchChatId, persistVersion]);

  useEffect(() => {
    if (!buildTimeline) return;
    const id = streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId;
    if (!id) return;
    if (buildSessionPersistTimerRef.current !== null) {
      clearTimeout(buildSessionPersistTimerRef.current);
    }
    buildSessionPersistTimerRef.current = setTimeout(() => {
      buildSessionPersistTimerRef.current = null;
      void patchBuildSessionApi({
        buildTimeline: buildTimeline as unknown as Record<string, unknown>,
      });
    }, BUILD_SESSION_PATCH_DEBOUNCE_MS);
    return () => {
      if (buildSessionPersistTimerRef.current !== null) {
        clearTimeout(buildSessionPersistTimerRef.current);
        buildSessionPersistTimerRef.current = null;
      }
    };
  }, [buildTimeline, patchBuildSessionApi, streamingLlmInteractionId]);

  /**
   * `DefaultChatTransport` POSTs JSON that includes AI SDK transcript `messages`; ideoz-ai-api
   * merges Flask-built prototyper context into the first user turn before forwarding to Chef.
   */
  const transport = useMemo(() => {
    const placeholder =
      typeof window !== 'undefined'
        ? `${window.location.origin}/__workbench_build_stream_pending__`
        : '/__workbench_build_stream_pending__';
    return new DefaultChatTransport({
      api: apiUrl || placeholder,
      // Turns a non-2xx response into a typed BuildStreamHttpError (status + contract code)
      // instead of the SDK's plain Error(responseText), so onError can decide on retries.
      // A 401 mid-turn (the access token expired during a long build) refreshes the token once
      // and retries the same request (FE-3).
      fetch: createBuildStreamFetch({
        refreshAccessToken: async () => {
          const token = await refreshAccessToken();
          if (token) accessTokenRef.current = token;
          return token;
        },
      }),
      headers: () => ({
        Authorization: `Bearer ${accessTokenRef.current}`,
      }),
      // Same body the SDK builds by default, with file bodies from earlier steps of the turn
      // replaced by placeholders, so continuation POSTs stay flat in size (P3.4).
      prepareSendMessagesRequest: ({ id, messages, body, trigger, messageId }) => {
        // The element the founder picked in the editor goes with the message they just wrote,
        // and only with that one: continuation rounds of the same turn carry it no further.
        const ask = trigger === 'submit-message' ? takePendingAsk() : null;
        return {
          body: {
            ...body,
            id,
            messages: trimTranscriptForRequest(messages),
            trigger,
            messageId,
            ...(ask ? { elementContext: ask.context } : {}),
            ...(ask?.screenshot ? { elementScreenshot: ask.screenshot } : {}),
          },
        };
      },
      body: () => {
        // A crash repair sends the hand edits it captured when it started (it cleared their
        // flags so the model may change them); every other build sends the current ones.
        const repairTurn =
          crashRepairRef.current &&
          crashRepairRef.current.llmInteractionId === streamingBuildLlmInteractionIdRef?.current
            ? crashRepairRef.current
            : null;
        const manual = repairTurn ? repairTurn.manual : collectWorkbenchManualFilesForRequest();
        const manualDiffs = repairTurn
          ? repairTurn.manualDiffs
          : collectWorkbenchManualFileDiffsForRequest();
        const manualHashes = repairTurn
          ? repairTurn.manualHashes
          : collectWorkbenchManualFileHashesForRequest(manual);
        const base: Record<string, unknown> = {
          modelProvider: modelRef.current.modelProvider,
          modelChoice: modelRef.current.modelChoice,
          ...(chefProjectIdForTurn(streamingBuildLlmInteractionIdRef?.current)
            ? { projectId: chefProjectIdForTurn(streamingBuildLlmInteractionIdRef?.current) }
            : {}),
          ...(streamingBuildLlmInteractionIdRef?.current
            ? { llmInteractionId: streamingBuildLlmInteractionIdRef?.current }
            : {}),
          // An automatic fix of this turn's failed version (P6.4b). The AI API only reads it
          // on round 1; continuation rounds keep the kind stored on the version.
          ...(fixTurnIdRef.current &&
          fixTurnIdRef.current === streamingBuildLlmInteractionIdRef?.current
            ? { turnKind: 'fix' }
            : {}),
          ...(crashRepairRef.current &&
          crashRepairRef.current.llmInteractionId === streamingBuildLlmInteractionIdRef?.current
            ? { repair: crashRepairRef.current.repair }
            : {}),
          ...(SERVER_WRITES_ENABLED ? { serverWrites: true } : {}),
          ...(SERVER_BUILD_ENABLED ? { serverBuild: true } : {}),
          ...(manual ? { workbenchManualFiles: manual } : {}),
          ...(manualDiffs ? { workbenchManualFileDiffs: manualDiffs } : {}),
          ...(manualHashes ? { workbenchManualFileHashes: manualHashes } : {}),
        };
        return base;
      },
    });
  }, [apiUrl]);

  const applyAnnotationChunk = useCallback(
    (raw: unknown) => {
      const chunks = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
      for (const item of chunks) {
        if (!item || typeof item !== 'object') continue;
        const c = item as Record<string, unknown>;
        if (c.type === 'assetFiles' && Array.isArray(c.files)) {
          for (const item of c.files) {
            if (!item || typeof item !== 'object') continue;
            const { path, base64, mimeType } = item as {
              path?: string;
              base64?: string;
              mimeType?: string;
            };
            if (typeof path !== 'string' || typeof base64 !== 'string') continue;
            if (writtenAssetPathsRef.current.has(path)) continue;
            writtenAssetPathsRef.current.add(path);

            {
              const capturedTurn = turnSessionRef.current.generation;
              assetWriteChainRef.current = assetWriteChainRef.current
                .then(async () => {
                  if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
                  const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
                  await persistAssetFile(wc, path, base64, mimeType);
                })
                .catch((err) => {
                  console.warn('[Workbench] assetFiles write failed', path, err);
                });
            }
          }
          continue;
        }

        const chunkKey = JSON.stringify(c);
        if (processedAnnotationsRef.current.has(chunkKey)) continue;
        processedAnnotationsRef.current.add(chunkKey);

        if (c.type === 'demoLinkCreated') {
          const rawId = c.prototyperDemoLinkId;
          const id = typeof rawId === 'number' ? rawId : Number(rawId);
          if (Number.isFinite(id)) {
            onStreamDemoLinkId?.(id);
            const lid = streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId;
            if (lid) {
              onPatchMessageByLlmInteractionId?.(lid, { prototyper_demo_link_id: id });
            }
          }
        }

        if (c.type === 'brandLogo' && typeof c.src === 'string') {
          workbenchStore.brandLogoSrc.set(c.src);
        }

        if (c.type === 'projectCreated' && typeof c.projectId === 'string') {
          chefProjectIdRef.current = c.projectId;
          setPersistVersion((v) => v + 1);
          onProjectCreated?.(c.projectId);
        }

        if (c.type === 'postBuildSpecVerification') {
          // Advisory only — do not surface in the timeline once files were generated.
          continue;
        }

        if (c.type === 'planComplete' && c.plan && typeof c.plan === 'object') {
          const plan = c.plan as Record<string, unknown>;
          editPlannedFilePathsRef.current = plannedPathsFromPlanComplete(plan);
          const coverageOk = plan.coverageOk === true;
          const rawGaps = plan.coverageGaps;
          const gaps = Array.isArray(rawGaps) ? rawGaps : [];
          const base = buildTimelineRef.current ?? createEmptyTimeline();
          const next = applyPlanCompleteToTimeline(
            base,
            plan,
            coverageOk,
            gaps,
          );
          timelineAnnotationRef.current = next;
          buildTimelineRef.current = next;
          setBuildTimeline(next);
        }

        if (c.type === 'fileRead' && typeof c.path === 'string') {
          const base = buildTimelineRef.current ?? createEmptyTimeline();
          const lineCount = typeof c.lineCount === 'number' ? c.lineCount : undefined;
          const key =
            typeof c.toolCallId === 'string' ? `read-${c.toolCallId}` : `read-${c.path}-${Date.now()}`;
          const next = applyExploreEventToTimeline(base, {
            key,
            title: `Read ${c.path}`,
            detail: lineCount !== undefined ? `${lineCount} lines` : undefined,
            status: 'done',
            toolName: 'readFile',
          });
          timelineAnnotationRef.current = next;
          buildTimelineRef.current = next;
          setBuildTimeline(next);
        }

        if (c.type === 'fileSearch' && typeof c.query === 'string') {
          const base = buildTimelineRef.current ?? createEmptyTimeline();
          const hitCount = typeof c.hitCount === 'number' ? c.hitCount : 0;
          const key =
            typeof c.toolCallId === 'string'
              ? `search-${c.toolCallId}`
              : `search-${c.query}-${Date.now()}`;
          const pathGlob = typeof c.pathGlob === 'string' ? c.pathGlob : undefined;
          const next = applyExploreEventToTimeline(base, {
            key,
            title: `Search "${c.query}"`,
            detail:
              hitCount > 0
                ? `${hitCount} hit(s)${pathGlob ? ` in ${pathGlob}` : ''}`
                : `No matches${pathGlob ? ` in ${pathGlob}` : ''}`,
            status: 'done',
            toolName: 'searchFiles',
          });
          timelineAnnotationRef.current = next;
          buildTimelineRef.current = next;
          setBuildTimeline(next);
        }

        if (c.type === 'serverExecution') {
          serverWritesTurnRef.current = c.writes === true;
          serverBuildTurnRef.current = c.build === true;
          serverWriteDeliveryRef.current = c.writeDelivery === 'client' ? 'client' : 'annotation';
        }

        /**
         * Chef executed this write and streamed it here. The tool handler never sees these
         * calls — the AI SDK does not invoke it for tools the server ran — so without this the
         * files would exist only on the server: an empty code view and a preview still showing
         * the starter template.
         */
        if (c.type === 'serverWrite' && typeof c.path === 'string') {
          const path = c.path;
          const kind = c.kind === 'delete' ? 'delete' : 'write';
          const content = typeof c.content === 'string' ? c.content : '';
          const capturedTurn = turnSessionRef.current.generation;
          assetWriteChainRef.current = assetWriteChainRef.current
            .then(async () => {
              if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
              const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
              if (kind === 'delete') {
                await executeDeleteFile(wc, path);
                return;
              }
              await persistUserFile(wc, path, content, false);
            })
            .catch((err) => {
              console.warn('[Workbench] applying a Chef-executed write failed', path, err);
              serverWriteDriftRef.current.push(path);
            });
        }

        if (c.type === 'serverBuild') {
          handleServerBuildEventRef.current(c);
        }

        // Seed the WebContainer with the last saved project files at the start of an
        // edit turn. WebContainer is empty on a fresh browser session even though the
        // project exists in the DB — without seeding, searchReplace calls find nothing.
        if (c.type === 'pruneWorkspace' && Array.isArray(c.keep)) {
          const keep = c.keep.filter((p): p is string => typeof p === 'string');
          {
            const capturedTurn = turnSessionRef.current.generation;
            assetWriteChainRef.current = assetWriteChainRef.current
              .then(async () => {
                if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
                const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
                await executePrune(wc, keep);
              })
              .catch((err) => {
                console.warn('[Workbench] pruneWorkspace failed', err);
              });
          }
        }

        if (c.type === 'seedFiles' && c.files && typeof c.files === 'object') {
          const seedMap = c.files as Record<string, string>;
          {
            const capturedTurn = turnSessionRef.current.generation;
            assetWriteChainRef.current = assetWriteChainRef.current
              .then(async () => {
                if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
                const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();

                for (const [filePath, content] of Object.entries(seedMap)) {
                  if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
                  if (typeof content !== 'string') continue;
                  const existing = workbenchStore.files.get()[filePath];
                  if (existing?.modified) continue;
                  await persistUserFile(wc, filePath, content, false);
                }
              })
              .catch((err) => {
                console.warn('[Workbench] seedFiles write failed', err);
              });
          }
        }
      }
    },
    [onProjectCreated, onStreamDemoLinkId, onPatchMessageByLlmInteractionId, streamingLlmInteractionId],
  );

  const onDataPart = useCallback(
    (part: unknown) => {
      if (!part || typeof part !== 'object') return;
      const p = part as Record<string, unknown>;
      const t = p.type;
      if (t === 'data-prototyperError') {
        // Sent by Nest/Flask right before the standard `error` chunk; `onError` reads it.
        const parsed = parseBuildStreamErrorData(p.data);
        if (parsed) {
          pendingStreamErrorPartRef.current = parsed;
        }
        return;
      }
      if (t === 'data-prototyperUsage') {
        const usage = Array.isArray(p.data) ? p.data[0] : undefined;
        const finishReason =
          usage && typeof usage === 'object'
            ? (usage as Record<string, unknown>).finishReason
            : undefined;
        streamTruncatedRef.current = finishReason === 'length';
        onCreditsRefresh?.();
        return;
      }
      if (t === 'data-prototyperAnnotation') {
        applyAnnotationChunk(p.data);
      }
      if (typeof t === 'string' && t.startsWith('data-') && 'data' in p) {
        const data = p.data;
        if (Array.isArray(data) || (data && typeof data === 'object')) {
          applyAnnotationChunk(data);
        }
      }
    },
    [applyAnnotationChunk, onCreditsRefresh],
  );

  /**
   * Mirrors one Chef-executed write in the WebContainer (P6.4d-3). Chef's result goes to the model;
   * a write that fails here is remembered and reported with the next build.
   */
  const applyServerExecutedWrite = async (toolCall: Parameters<typeof getToolInput>[0]): Promise<void> => {
    const { toolName, toolCallId } = toolCall as unknown as { toolName: string; toolCallId: string };
    const input = getToolInput(toolCall);
    const path = String(input.path ?? '');
    const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
    const turnSignal = turnAbortControllerRef.current.signal;
    let output: string;
    try {
      if (toolName === 'writeFile') {
        output = await executeWriteFile(wc, path, String(input.content ?? ''), turnSignal);
      } else if (toolName === 'searchReplace') {
        output = await executeSearchReplace(
          wc,
          path,
          String(input.oldString ?? ''),
          String(input.newString ?? ''),
          input.replaceAll === true,
          turnSignal,
        );
      } else {
        output = await executeDeleteFile(wc, path, turnSignal);
      }
    } catch (err) {
      output = `Error: ${err instanceof Error ? err.message : String(err)}`;
    }
    if (output.startsWith('Error')) {
      serverWriteDriftRef.current.push(`${toolName} ${path}: ${output}`);
    }
    setToolSteps((prev) => {
      const next = capToolSteps([
        ...prev,
        { key: toolCallId, toolName, args: input, isComplete: true, result: output },
      ]);
      toolStepsRef.current = next;
      return next;
    });
  };
  const applyServerExecutedWriteRef = useRef(applyServerExecutedWrite);
  applyServerExecutedWriteRef.current = applyServerExecutedWrite;

  const { messages, sendMessage, addToolOutput, stop, status } = useChat({
    id: workbenchChatId,
    transport,
    /**
     * Disabled: the SDK can fire multiple continuation paths. We continue explicitly in
     * `scheduleContinueAfterClientTools` after each client tool returns, when the assistant
     * stream is idle (`chatStatusRef` not streaming/submitted) so we do not POST before
     * more tool parts arrive.
     */
    sendAutomaticallyWhen: () => false,
    onToolCall: async ({ toolCall }) => {
      await assetWriteChainRef.current;

      // readFile/searchFiles execute on the server — skip client handling entirely.
      if (isServerExecutedPrototyperTool(toolCall.toolName)) {
        return;
      }

      // F1: Chef ran the build and reported it through annotations; nothing to run here.
      if (serverBuildTurnRef.current && toolCall.toolName === 'build') {
        return;
      }

      // P6.4d-3: Chef already ran this write; mirror it here. Chef's result goes back to the
      // model, so no tool output and no continuation round from the browser. Skipped when Chef
      // streams its writes as annotations, which apply them already.
      if (
        serverWritesTurnRef.current &&
        serverWriteDeliveryRef.current !== 'annotation' &&
        SERVER_WRITE_TOOLS.has(toolCall.toolName)
      ) {
        if (turnSessionRef.current.aborted) return;
        await applyServerExecutedWriteRef.current(toolCall);
        return;
      }

      if (turnSessionRef.current.aborted) {
        // The turn this call belongs to was explicitly aborted (conversation switch or
        // stream cancel) — ignore it rather than mutate a now-unrelated chat's WebContainer.
        // Belt-and-suspenders alongside the stream-abort effect below: closes the small
        // window between the underlying fetch being aborted and this callback actually
        // stopping being invoked.
        return;
      }

      const toolName = toolCall.toolName;
      const toolCallId = toolCall.toolCallId;
      const capturedTurn = turnSessionRef.current.generation;
      const turnSignal = turnAbortControllerRef.current.signal;
      toolTurnGenerationRef.current = capturedTurn;
      clientToolsInFlightRef.current += 1;
      workbenchStore.clientToolsBusy.set(true);

      const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
      const input = getToolInput(toolCall);

      const stepKey = `${toolCallId}`;
      setToolSteps((prev) => {
        const next = capToolSteps([
          ...prev,
          { key: stepKey, toolName, args: input, isComplete: false },
        ]);
        toolStepsRef.current = next;
        return next;
      });

      /** `continueTurn: false`: record the result without asking the model for another round. */
      const completeToolStep = (
        result: string,
        isError = false,
        opts: { continueTurn?: boolean } = {},
      ): void => {
        clientToolsInFlightRef.current = Math.max(0, clientToolsInFlightRef.current - 1);
        if (clientToolsInFlightRef.current === 0) {
          workbenchStore.clientToolsBusy.set(false);
        }
        if (isTurnStale(turnSessionRef.current, capturedTurn)) {
          return;
        }
        if (opts.continueTurn !== false) {
          toolResultContinuationPendingRef.current = true;
          wakeStreamContinuation();
        }
        setToolSteps((prev) => {
          const next = capToolSteps(
            prev.map((s) =>
              s.key === stepKey ? { ...s, result, isComplete: true } : s,
            ),
          );
          toolStepsRef.current = next;
          return next;
        });
        if (isError) {
          deferAddToolOutput(
            {
              tool: toolName,
              toolCallId,
              state: 'output-error',
              errorText: result,
            },
            capturedTurn,
          );
        } else {
          deferAddToolOutput({ tool: toolName, toolCallId, output: result }, capturedTurn);
        }
      };

      try {
        let output: string;

        if (toolName === 'writeFile') {
          const path = String(input.path ?? '');
          const content = String(input.content ?? '');
          output = await executeWriteFile(wc, path, content, turnSignal);
        } else if (toolName === 'searchReplace') {
          output = await executeSearchReplace(
            wc,
            String(input.path ?? ''),
            String(input.oldString ?? ''),
            String(input.newString ?? ''),
            input.replaceAll === true,
            turnSignal,
          );
        } else if (toolName === 'deleteFile') {
          output = await executeDeleteFile(wc, String(input.path ?? ''), turnSignal);
        } else if (toolName === 'build') {
          if (turnSignal.aborted) {
            completeToolStep('Skipped: turn was cancelled before the build could start.', true);
            return;
          }
          if (!wc) {
            // Only the build needs a Node runtime, and the light preview has none: this turn
            // asked the browser to verify itself in a tab that cannot. That is an environment
            // problem (the server build is off, or Chef did not run it), never the model's
            // code, so it must not be answered with a paid fix stream.
            workbenchStore.buildStatus.set('error');
            completeToolStep(
              'Error: this browser cannot build the prototype (the light preview runs no Node ' +
                'runtime). The server build has to verify this turn.',
              true,
              { continueTurn: false },
            );
            endTurnAfterEnvironmentFailureRef.current(
              streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId,
            );
            return;
          }

          // Chef has finished writing every file for this turn — persist them now, keyed to
          // this turn, *before* the (30-90s) build, so a refresh/lost-tab mid-build no longer
          // loses the turn. Concurrent, never awaited: it must not delay or be able to break
          // the build (see `force` on runAutoPrototypeSnapshot for why it isn't dropped by the
          // isStreaming/clientToolsBusy guard that would otherwise silently swallow it here).
          const preBuildLid = streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId;
          if (preBuildLid) {
            void runAutoPrototypeSnapshot('build-start', {
              buildVerified: false,
              force: true,
              llmInteractionId: preBuildLid,
            }).catch((err) => {
              console.warn('[Workbench] pre-build snapshot failed', err);
            });
          }

          // Idempotent: also covers a continuation round, where the Chef project is now known.
          const buildTurnCtx = versionTurnContext(preBuildLid);
          if (buildTurnCtx) {
            void versionTrackerRef.current?.begin(buildTurnCtx, chefProjectIdForTurn(buildTurnCtx.llmInteractionId));
          }

          workbenchStore.buildStatus.set('building');
          try {
            const result = await executeBuild(wc, undefined, turnSignal);
            if (isTurnStale(turnSessionRef.current, capturedTurn)) {
              // A stale turn's build result must not flip the *current* conversation's
              // buildStatus/repeat-error counters — those belong to whatever turn is active now.
              output = result.formattedOutput;
              completeToolStep(output, !result.ok);
              return;
            }
            if (result.environment) {
              workbenchStore.buildStatus.set('error');
              completeToolStep(result.formattedOutput, true, { continueTurn: false });
              endTurnAfterEnvironmentFailureRef.current(preBuildLid);
              return;
            }
            workbenchStore.buildStatus.set(result.ok ? 'success' : 'error');
            if (result.ok) workbenchStore.hasUnbuiltEdits.set(false);

            let buildOutput = result.formattedOutput;
            if (serverWriteDriftRef.current.length > 0) {
              // Chef applied these writes but the browser could not, so this build ran on other
              // files: ask the model to write those files whole.
              buildOutput =
                'Note: these edits did not apply in the preview workspace; rewrite each of these files in full with writeFile:\n' +
                `${serverWriteDriftRef.current.join('\n')}\n\n${buildOutput}`;
              serverWriteDriftRef.current = [];
            }
            if (!result.ok) {
              const rep = buildRepeatRef.current;
              if (result.errorSig === rep.lastSig) {
                rep.count++;
              } else {
                rep.lastSig = result.errorSig;
                rep.count = 1;
              }
              if (rep.count >= MAX_BUILD_REPEAT_ERRORS) {
                const hasDirtyManualDrafts =
                  Object.keys(workbenchStore.getModifiedFiles()).length > 0;
                buildOutput += hasDirtyManualDrafts
                  ? `\n\nSTOP retrying — the same error has occurred ${rep.count} times. ` +
                    `Prefer minimal surgical edits; do NOT wholesale-rewrite paths the user edited in IDE unless unavoidable.`
                  : `\n\nSTOP retrying — the same error has occurred ${rep.count} times. ` +
                    `Rewrite the affected file(s) from scratch instead of patching.`;
              }
            } else {
              buildRepeatRef.current = { lastSig: '', count: 0 };
              queueMicrotask(() => {
                void runAutoPrototypeSnapshot('build', {
                  buildVerified: true,
                  force: true,
                  llmInteractionId: preBuildLid,
                });
              });
            }
            output = buildOutput;
          } finally {
            // Never touch buildStatus on behalf of a turn that's since been superseded —
            // it may now belong to a different, currently-active conversation.
            if (
              !isTurnStale(turnSessionRef.current, capturedTurn) &&
              workbenchStore.buildStatus.get() === 'building'
            ) {
              workbenchStore.buildStatus.set('error');
            }
          }
        } else if (toolName === 'configureSession') {
          const mode = String(input.mode ?? 'website');
          const skills = Array.isArray(input.skills)
            ? (input.skills as string[])
            : undefined;
          output = executeConfigureSession({ mode, skills });
        } else {
          completeToolStep(`Unknown tool: ${toolName}`, true);
          return;
        }

        completeToolStep(output);
      } catch (e) {
        completeToolStep(e instanceof Error ? e.message : 'Tool execution failed', true);
      }
    },
    onData: (dataPart) => {
      onDataPart(dataPart);
    },
    onError: (error) => {
      // Prefer the code the server sent in-stream; otherwise classify the thrown error
      // (a BuildStreamHttpError from fetchBuildStream, a network TypeError, or an SDK Error).
      const classified = pendingStreamErrorPartRef.current ?? classifyBuildStreamError(error);
      pendingStreamErrorPartRef.current = null;
      lastStreamErrorRef.current = classified;
      console.warn('[Workbench] build-stream error', classified);

      /**
       * A failure this turn is about to retry is not news. Reporting it immediately flashed "The
       * prototype build failed. Try again." across the top of the panel at the start of a turn
       * that then ran perfectly — the retry succeeded a second later and cleared it. It is shown
       * only once nothing is going to have another go: a code that retrying cannot help, or a
       * turn that has used its retries.
       */
      const willRetryTransport =
        retryPolicyFor(classified.code) !== 'none' &&
        transportRetryCountRef.current < MAX_BUILD_STREAM_TRANSPORT_RETRIES &&
        buildRepeatRef.current.count < MAX_BUILD_REPEAT_ERRORS &&
        shouldAutoContinueAfterFailedBuild(messagesRef.current, toolStepsRef.current);
      if (!willRetryTransport) {
        setWorkbenchPersistError(userMessageFor(classified.code));
      }

      if (classified.code === 'FIX_LIMIT_REACHED') {
        // No attempt was left when the fix stream started (the counts moved on, e.g. in
        // another tab): show the card with the version's current counts, not an error.
        setWorkbenchPersistError(null);
        const limitCtx = versionTurnContext(
          fixTurnIdRef.current ?? streamingBuildLlmInteractionIdRef?.current
        );
        fixTurnIdRef.current = null;
        setAutoFixLabel(null);
        if (limitCtx) {
          void versionTrackerRef.current?.current(limitCtx).then((version) => {
            if (!version) return;
            if (projectConversationRef.current.conversationId !== limitCtx.conversationId) return;
            setVersionAttention({
              llmInteractionId: limitCtx.llmInteractionId,
              versionId: version.id,
              fixAttempts: version.fixAttempts ?? 0,
              fixAttemptsGranted: version.fixAttemptsGranted ?? 0,
            });
          });
        }
        return;
      }

      // Credits, ownership, validation, context size and an expired session never change
      // by retrying — `willRetryTransport` is false for those, and for a turn out of retries.
      if (!willRetryTransport) return;
      const attempt = transportRetryCountRef.current;
      transportRetryCountRef.current += 1;
      const capturedTurn = turnSessionRef.current.generation;
      window.setTimeout(() => {
        if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
        requestStreamContinuation();
      }, backoffDelayMs(attempt));
    },
    onFinish: async ({
      messages: finishMessages,
      isAbort,
      isError,
      isDisconnect,
    }) => {
      messagesRef.current = finishMessages;
      onCreditsRefresh?.();

      const lid = streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId;
      // Chef wrote this turn's files: the workspace has to hold them before anything below
      // snapshots, verifies or resumes from what the browser thinks it has.
      if (serverWritesTurnRef.current) {
        await syncTurnFilesFromServerRef.current(lid);
      }
      const toolBuildFailed = lastToolStepBuildFailed(toolStepsRef.current);
      let buildOk =
        workbenchStore.buildStatus.get() === 'success' && !toolBuildFailed;
      // Stop after this turn's build had passed finishes the turn; see `stoppedTurnOutcome`.
      const stopped = stoppedTurnOutcome({
        isAbort: Boolean(isAbort),
        isError: Boolean(isError),
        isDisconnect: Boolean(isDisconnect),
        buildOk,
        toolSteps: toolStepsRef.current,
      });
      const transportFailed =
        Boolean(isAbort || isError || isDisconnect) && stopped !== 'build-passed';

      // Auto-continue after a failed build/type-check (unless the user aborted).
      // A transport failure (isError/isDisconnect) is retried by `onError` with backoff and
      // a per-turn cap, and never for non-retryable codes — retrying here as well would
      // re-POST immediately and bypass both.
      if (!isAbort && !isError && !isDisconnect && tryAutoContinueAfterFailedBuild(finishMessages)) {
        return;
      }

      // Resume a turn that hit the output-token ceiling before it could call `build`.
      if (!transportFailed && !buildOk && tryAutoContinueAfterTruncation()) {
        return;
      }

      // Model wrote files but never called build — compile once client-side (last resort only).
      if (!transportFailed && !buildOk) {
        const completenessInput = getEditLoopCompletenessInput(finishMessages);
        if (allowOpportunisticBuild(completenessInput)) {
          const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
          if (wc) {
            workbenchStore.buildStatus.set('building');
            try {
              const result = await executeBuild(wc);
              workbenchStore.buildStatus.set(result.ok ? 'success' : 'error');
              if (result.environment) {
                endTurnAfterEnvironmentFailureRef.current(lid);
                return;
              }
              if (result.ok) {
                workbenchStore.hasUnbuiltEdits.set(false);
                buildOk = true;
                buildRepeatRef.current = { lastSig: '', count: 0 };
                queueMicrotask(() => {
                  void runAutoPrototypeSnapshot('build', {
                    buildVerified: true,
                    force: true,
                    llmInteractionId: lid,
                  });
                });
              }
            } catch {
              workbenchStore.buildStatus.set('error');
            }
          }
        }
      }

      // Planned edit work or build still open — keep the multi-round client-tool loop alive.
      if (
        !transportFailed &&
        !buildOk &&
        isEditTurnUnfinished(getEditLoopCompletenessInput(finishMessages))
      ) {
        requestStreamContinuation();
        return;
      }

      const turnSucceeded = !transportFailed && buildOk;
      // The turn ended: no automatic fix is running any more (a new one may start below).
      setAutoFixLabel(null);
      crashRepairRef.current = null;
      if (turnSucceeded) {
        fixTurnIdRef.current = null;
        if (lid) turnBudgetsRef.current.forget(lid);
      }

      if (lid) {
        // Recompute from `toolStepsRef.current` rather than trusting `buildTimelineRef.current`:
        // the displayed timeline is normally rebuilt by a `requestAnimationFrame`-scheduled
        // effect (below) that watches `toolSteps`, so it can still be a frame or two behind the
        // very last tool call (e.g. `build`) when this stream's `onFinish` fires synchronously —
        // and once finalized here, `patchMessageByLlmInteractionId`/`patchBuildSessionApi` freeze
        // this as the permanent, non-live version of the timeline. Without this, the persisted
        // timeline can be missing whichever "writing files" / "build" steps hadn't made it
        // through that RAF yet, and there is no later live render to backfill them.
        const freshSteps = toolStepsRef.current.map((s) => ({
          key: s.key,
          toolName: s.toolName,
          isComplete: s.isComplete,
          args: s.args,
          result: s.result,
        }));
        const freshBase = timelineFromToolSteps(
          freshSteps,
          freshSteps.length > 0 ? undefined : messagesRef.current,
        );
        const freshMerged = mergeTimelinePreservingAnnotations(
          freshBase,
          timelineAnnotationRef.current,
        );
        const base = freshMerged.segments.length
          ? freshMerged
          : (buildTimelineRef.current ?? createEmptyTimeline());
        let finalT: BuildTimelineV2;
        let sessionStatus: string;
        if (transportFailed) {
          finalT = markStreamError(base);
          sessionStatus = 'error';
        } else if (!buildOk) {
          finalT = markStreamEndedWithoutSuccessfulBuild(base);
          sessionStatus = 'error';
        } else {
          finalT = markStreamSuccess(base);
          sessionStatus = 'success';
        }
        buildTimelineRef.current = finalT;
        setBuildTimeline(finalT);
        const completedAt = new Date().toISOString();
        void patchBuildSessionApi({
          buildTimeline: finalT as unknown as Record<string, unknown>,
          buildStatus: sessionStatus,
          buildCompletedAt: completedAt,
        });
        onPatchMessageByLlmInteractionId?.(lid, {
          buildTimeline: finalT,
          buildStatus: sessionStatus,
          prototypeDisplayName:
            getPrototypeDisplayName?.() ?? buildSessionPrototypeMetaRef.current.prototypeDisplayName,
          // Never show the "Ideoz AI has completed creating the prototyper
          // successfully" banner — the message text + the prototype pill already
          // convey completion. (Error banners are handled separately in
          // ConversationModule via `prototypeError`.)
          hasBanner: false,
          bannerType: undefined,
          drawType: undefined,
          bannerPosition: undefined,
        });
        if (turnSucceeded) {
          // Turn is only marked "built" once the snapshot is confirmed persisted —
          // see the `onBuildStreamTurnCompleted` call inside `runAutoPrototypeSnapshot`.
          setActiveSnapshotInteractionId(lid);
        } else if (!transportFailed) {
          // Ended without a passing build: a code failure for the version. A transport failure
          // keeps the draft, so resume or retry can still finish it.
          const failedCtx = versionTurnContext(lid);
          // While the preview workspace is being recovered (or could not be), the build could
          // not run: record an environment failure, which keeps the draft and starts no paid fix.
          const workspaceDown =
            workbenchStore.previewRecovery.get().status !== 'idle' ||
            serverBuildEnvironmentFailureRef.current;
          if (failedCtx && workspaceDown) {
            void versionTrackerRef.current?.failed(failedCtx, { kind: 'environment' });
          } else if (failedCtx) {
            const failedTurn = turnSessionRef.current.generation;
            void versionTrackerRef.current
              ?.failed(failedCtx, {
                kind: 'code',
                errorSignature: buildRepeatRef.current.lastSig || null,
                buildLog: lastBuildStepOutput(toolStepsRef.current),
              })
              .then((failure) => {
                // Automatic fix or the card (P6.4b), unless another turn started meanwhile.
                if (isTurnStale(turnSessionRef.current, failedTurn)) return;
                handleVersionFailureRef.current(failedCtx, failure);
              });
          }
        }
      }

      if (isAbort || isError || isDisconnect) {
        if (stopped === 'build-passed') {
          // Its verified snapshot, as a finished turn saves at stream end (below). Forced, so the
          // streaming gate does not drop it.
          void runAutoPrototypeSnapshot('stream-end', {
            buildVerified: true,
            force: true,
            llmInteractionId: lid,
          });
        }
        if (!suppressStreamAbortRef.current) {
          abortCurrentTurn();
          clearDeferToolOutputTimeouts();
        }
        workbenchStore.isStreaming.set(false);
        if (stopped === 'unfinished') settleStoppedBuild(lid);
        return;
      }
      if (turnSucceeded) {
        cancelStreamContinuationRef.current?.();
        cancelStreamContinuationRef.current = null;
        toolResultContinuationPendingRef.current = false;
        streamTruncatedRef.current = false;
        truncationContinueRef.current = 0;
        void runAutoPrototypeSnapshot('stream-end', {
          buildVerified: true,
          force: true,
          llmInteractionId: lid,
        });
      }
      const clearIfNoPendingTools = async (msgs: readonly unknown[]): Promise<void> => {
        if (lastAssistantHasPendingClientTools(msgs)) return;
        const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
        if (wc) {
          try {
            await flushDirtyEditorFilesBeforeStream(wc);
          } catch (err) {
            console.warn('[Workbench] post-stream manual edit flush failed', err);
          }
        }
        workbenchStore.isStreaming.set(false);
        workbenchStore.clearManualEditConsumedFlags();
      };
      void clearIfNoPendingTools(finishMessages);
      if (lastAssistantHasPendingClientTools(finishMessages)) {
        queueMicrotask(() => {
          void clearIfNoPendingTools(messagesRef.current);
          if (lastAssistantHasPendingClientTools(messagesRef.current)) {
            requestAnimationFrame(() => {
              void clearIfNoPendingTools(messagesRef.current);
            });
          }
        });
      }
    },
  });

  chatStatusRef.current = status;
  useEffect(() => {
    // A continuation waiting for the chat to settle runs as soon as it does (FE-7).
    continuationWakersRef.current.forEach((wake) => wake());
  }, [status]);

  addToolOutputRef.current = addToolOutput;
  sendMessageRef.current = sendMessage as unknown as (msg?: unknown) => void;

  useEffect(() => {
    toolStepsRef.current = toolSteps;
  }, [toolSteps]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (timelineRebuildRafRef.current !== null) {
      cancelAnimationFrame(timelineRebuildRafRef.current);
    }
    timelineRebuildRafRef.current = requestAnimationFrame(() => {
      timelineRebuildRafRef.current = null;
      const steps = toolSteps.map((s) => ({
        key: s.key,
        toolName: s.toolName,
        isComplete: s.isComplete,
        args: s.args,
        result: s.result,
      }));
      const base = timelineFromToolSteps(steps, steps.length > 0 ? undefined : messages);
      const productionBuildOk =
        buildStatus === 'success' && !lastToolStepBuildFailed(steps) && !designFixPending;
      const merged = mergeTimelinePreservingAnnotations(base, timelineAnnotationRef.current);
      const t = mergeLiveTerminalSegments(merged, {
        productionBuildOk,
        finalizeComplete: false,
      });
      setBuildTimeline(t);
      buildTimelineRef.current = t;
    });
    return () => {
      if (timelineRebuildRafRef.current !== null) {
        cancelAnimationFrame(timelineRebuildRafRef.current);
        timelineRebuildRafRef.current = null;
      }
    };
  }, [toolSteps, messages, buildStatus, designFixPending]);

  /**
   * Before a new turn (P6.4e): when another tab or device verified a newer version of the
   * prototype this tab has loaded, switch to it first so the turn does not start from older files.
   * (Chef's `seedFiles` refresh the files it edits, not the ones the newer version deleted.)
   * Best effort and bounded; it never blocks the turn for long.
   */
  const syncTabToCurrentVersion = async (
    turnLlmInteractionId: string | null | undefined,
  ): Promise<void> => {
    const { projectId: pid, conversationId: cid } = projectConversationRef.current;
    const loaded = activeSnapshotInteractionIdRef.current;
    if (!pid || !cid || !loaded || loaded === turnLlmInteractionId) return;
    if (turnLlmInteractionId && fixTurnIdRef.current === turnLlmInteractionId) return;
    if (Object.keys(workbenchStore.getModifiedFiles()).length > 0) return;
    try {
      const versions = await Promise.race([
        prototyperService.listConversationTurnVersions(pid, cid),
        new Promise<never>((_, reject) =>
          window.setTimeout(() => reject(new Error('timed out')), 3_000),
        ),
      ]);
      turnVersionsRef.current = indexTurnVersions(versions);
      const newer = newerVerifiedVersion(versions, loaded);
      if (newer?.llmInteractionId) {
        await restoreSnapshotRef.current(newer.llmInteractionId);
      }
    } catch (err) {
      console.warn('[Workbench] syncing to the current version failed', err);
    }
  };

  const startGeneration = useCallback(
    (prompt: string) => {
      if (!apiUrl) {
        return;
      }
      setWarmBootLatch((n) => n + 1);
      suppressStreamAbortRef.current = true;
      void stop();
      bumpTurnGeneration(turnSessionRef.current, false);
      resumeBuildAbortRef.current?.abort();
      resumeBuildAbortRef.current = null;
      clearDeferToolOutputTimeouts();

      const beginStream = (): void => {
        workbenchStore.isStreaming.set(true);
        workbenchStore.clientToolsBusy.set(false);
        workbenchStore.buildStatus.set('idle');
        recordProductionBuildOutcome(false);
        processedAnnotationsRef.current.clear();
        writtenAssetPathsRef.current.clear();
        assetWriteChainRef.current = Promise.resolve();
        buildRepeatRef.current = { lastSig: '', count: 0 };
        cancelStreamContinuationRef.current?.();
        cancelStreamContinuationRef.current = null;
        continuationSentRef.current = false;
        toolResultContinuationPendingRef.current = false;
        toolContinuationCountRef.current = 0;
        editPlannedFilePathsRef.current = [];
        streamTruncatedRef.current = false;
        truncationContinueRef.current = 0;
        pendingStreamErrorPartRef.current = null;
        lastStreamErrorRef.current = null;
        transportRetryCountRef.current = 0;
        timelineAnnotationRef.current = null;
        // A prior turn's "stream stalled" banner must not bleed into a fresh generation —
        // otherwise it sits stale over an otherwise-healthy new build until the user refreshes.
        setWorkbenchPersistError(null);
        workbenchStore.snapshotPreviewReady.set(false);
        // A follow-up generation in the same conversation must not inherit the *previous*
        // turn's "preview is visible" state — otherwise View Insights / Publish stay revealed
        // (stale-true) through the whole new build, since nothing else resets this atom until
        // `Workbench`'s own effect re-evaluates it, which only happens once its component tree
        // is actually re-rendering against the new build's state.
        workbenchStore.previewReady.set(false);

        const empty = createEmptyTimeline();
        setBuildTimeline(empty);
        buildTimelineRef.current = empty;

        const lid = streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId;
        // Any stream answers the card; a different turn also ends a pending automatic fix.
        // Requested in the body below; Chef's `serverExecution` annotation decides what is true.
        serverWritesTurnRef.current = false;
        serverBuildTurnRef.current = false;
        serverBuildEnvironmentFailureRef.current = false;
        serverWriteDriftRef.current = [];
        if (lid) turnBudgetsRef.current.start(lid, Date.now());
        setVersionAttention(null);
        if (fixTurnIdRef.current && fixTurnIdRef.current !== lid) {
          fixTurnIdRef.current = null;
          setAutoFixLabel(null);
        }
        if (lid) {
          const displayName = getPrototypeDisplayName?.();
          buildSessionPrototypeMetaRef.current = {
            prototypeDisplayName: displayName ?? null,
            prototypeVersionIndex: undefined,
          };
          const beginGeneration = turnSessionRef.current.generation;
          void (async () => {
            try {
              const body = {
                prototypeDisplayName: displayName,
                buildTimeline: empty as unknown as Record<string, unknown>,
              };
              const created = await prototyperService.createPrototyperBuildSession(lid, body);
              const row = created as {
                prototypeVersionIndex?: number | null;
                prototypeDisplayName?: string | null;
              };
              buildSessionPrototypeMetaRef.current.prototypeVersionIndex =
                row.prototypeVersionIndex ?? null;
              if (row.prototypeDisplayName != null) {
                buildSessionPrototypeMetaRef.current.prototypeDisplayName = row.prototypeDisplayName;
              }
            } catch (e) {
              console.warn('[Workbench] create prototyper build session failed', e);
            }
            // The version is the same row as the build session, so create it afterwards, then take
            // the turn's build lease before the first request (P6.4e): when another tab or device
            // is building this turn, this tab must not start a second generation.
            const draftCtx = versionTurnContext(lid);
            if (draftCtx) {
              const lease = await versionTrackerRef.current?.acquire(
                draftCtx,
                chefProjectIdForTurn(draftCtx.llmInteractionId),
              );
              if (lease === 'held-elsewhere') {
                workbenchStore.isStreaming.set(false);
                setWorkbenchPersistError(LEASE_HELD_ELSEWHERE_MESSAGE);
                return;
              }
            }
            if (isTurnStale(turnSessionRef.current, beginGeneration)) return;
            void sendMessageRef.current?.({ text: prompt });
          })();
        } else {
          void sendMessageRef.current?.({ text: prompt });
        }
        suppressStreamAbortRef.current = false;
      };

      const flushThenBegin = (): void => {
        const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
        if (wc && Object.keys(workbenchStore.getModifiedFiles()).length > 0) {
          void flushDirtyEditorFilesBeforeStream(wc)
            .catch((err) => {
              console.warn('[Workbench] pre-stream manual edit flush failed', err);
            })
            .finally(beginStream);
          return;
        }
        beginStream();
      };

      // A build-stream spends credits on tool calls only a WebContainer can run, so a device
      // that cannot boot one, or a tab that does not own it, must not start one (P2.3, P2.4).
      if (detectWorkbenchCapability().tier === 'view-only') {
        suppressStreamAbortRef.current = false;
        setWorkbenchPersistError('Building prototypes needs a desktop browser.');
        return;
      }
      void waitForTabOwnershipDecision().then((ownership) => {
        if (ownership === 'other-tab' || ownership === 'other-tab-busy' || ownership === 'released') {
          suppressStreamAbortRef.current = false;
          setWorkbenchPersistError(
            'The workbench is open in another tab. Choose "Use it here" to build in this tab.'
          );
          return;
        }
        void syncTabToCurrentVersion(
          streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId,
        ).finally(flushThenBegin);
      });
    },
    [
      apiUrl,
      getPrototypeDisplayName,
      projectId,
      streamingLlmInteractionId,
      stop,
      clearDeferToolOutputTimeouts,
    ],
  );

  const restorePrototypeSnapshotForInteraction = useCallback(
    async (llmInteractionId: string, hint?: PrototypeSnapshotRestoreHint) => {
      const { projectId: pid, conversationId: cid } = projectConversationRef.current;
      if (!pid || !cid) return;
      if (typeof window === 'undefined' || !window.crossOriginIsolated) return;

      if (
        !hint?.force &&
        activeSnapshotInteractionIdRef.current === llmInteractionId &&
        activeSnapshotIdRef.current
      ) {
        return;
      }

      if (workbenchStore.isStreaming.get() || workbenchStore.clientToolsBusy.get()) {
        throw new Error('Prototype is still building. Wait for the current build to finish.');
      }

      setWarmBootLatch((n) => n + 1);
      setRestoreInteractionLoadingId(llmInteractionId);

      try {
        return await withRestoreMutex(restoreMutexRef.current, llmInteractionId, async (signal) => {
          // Nothing to wait for with the light preview: the compiler reads the store this
          // restore writes, so waiting 45 s for a container that will never boot only turned
          // every version click into that timeout.
          if (!lightPreviewRuntimeRef.current) {
            const wc = await waitForWebContainerReady(
              () => webcontainerRef.current ?? workbenchStore.webcontainer.get(),
            );
            if (!wc) {
              throw new Error('WebContainer did not become ready in time');
            }
            await waitForTemplateReady();
          }
          let latest = await prototyperService.getPrototypeSnapshotByInteraction(
            pid,
            cid,
            llmInteractionId,
          );
          if (signal.aborted) {
            throw new DOMException('Restore aborted', 'AbortError');
          }
          // A version opened before in any tab of this browser comes from the local cache.
          let blob = await readCachedSnapshotZip(snapshotZipCacheKey(latest));
          if (!blob) {
            let res = await fetch(latest.downloadUrl, { signal });
            if (res.status === 403 || res.status === 404) {
              // One freshly signed URL: the first may have expired, or a newer snapshot of this
              // turn replaced the object behind it.
              latest = await prototyperService.getPrototypeSnapshotByInteraction(
                pid,
                cid,
                llmInteractionId,
              );
              if (signal.aborted) {
                throw new DOMException('Restore aborted', 'AbortError');
              }
              res = await fetch(latest.downloadUrl, { signal });
              if (res.status === 403 || res.status === 404) {
                throw new SnapshotObjectMissingError(llmInteractionId);
              }
            }
            if (!res.ok) {
              throw new Error(`Snapshot download failed (${res.status})`);
            }
            blob = await res.blob();
            void writeCachedSnapshotZip(snapshotZipCacheKey(latest), blob);
          }
          if (signal.aborted) {
            throw new DOMException('Restore aborted', 'AbortError');
          }
          await applySnapshotBlobToRuntime(blob, signal);
          const knownBuildSuccess =
            latest.buildVerified === true ||
            hint?.buildStatus === 'success' ||
            hint?.hasBuildTimeline === true;
          markSnapshotPreviewReady({
            buildVerified: latest.buildVerified === true,
            knownBuildSuccess,
          });
          setActiveSnapshotInteractionId(llmInteractionId);
          setActiveSnapshotId(latest.snapshotId);
          // The next manual-edit save starts from this revision (stale-save check, P6.4e).
          const indexedEntry = snapshotIndexRef.current.get(llmInteractionId);
          if (!indexedEntry || indexedEntry.revision < latest.revision) {
            snapshotIndexRef.current.set(llmInteractionId, {
              ...indexedEntry,
              llmInteractionId,
              snapshotId: latest.snapshotId,
              revision: latest.revision,
              createdAt: latest.createdAt,
              ...(latest.buildVerified ? { buildVerified: true } : {}),
            });
          }
          // Take ownership of `snapshotBootstrap` too. Starting this restore aborts any in-flight
          // boot-time bootstrap, and that one bails on `signal.aborted` *without* settling — on the
          // assumption the superseding call owns the outcome. It didn't, so the state stayed
          // 'pending' forever with nothing left to move it.
          setSnapshotBootstrap('restored');
          recordSnapshotFingerprintForInteraction(llmInteractionId, latest.buildVerified === true);
          return latest;
        });
      } finally {
        setRestoreInteractionLoadingId((current) =>
          current === llmInteractionId ? null : current,
        );
      }
    },
    [recordSnapshotFingerprintForInteraction],
  );
  restoreSnapshotRef.current = (llmInteractionId, hint) =>
    restorePrototypeSnapshotForInteraction(llmInteractionId, hint);

  /**
   * Finish a build that was interrupted (refresh / lost tab) after its files were snapshotted
   * at build-start but before the build was verified. Restores that turn's own files (NOT
   * whatever the boot-time bootstrap restored — it prefers the newest *verified* entry, which
   * may be an older turn), re-runs the build locally with no LLM stream, and uploads a verified
   * snapshot on success. Returns `'failed'` only for a genuine local build failure — callers
   * should fall back to a real LLM fix-stream only in that case, never for `'skipped'`.
   */
  /**
   * The part of a resumed build that is the same wherever the build ran: record the verified
   * snapshot, mark the session, and tell the conversation the turn is finished.
   */
  const finishResumedBuild = async (llmInteractionId: string): Promise<ResumeBuildOutcome> => {
    workbenchStore.hasUnbuiltEdits.set(false);
    await runAutoPrototypeSnapshot('resume-build', {
      buildVerified: true,
      force: true,
      llmInteractionId,
    });
    try {
      await prototyperService.patchPrototyperBuildSession(llmInteractionId, {
        buildStatus: 'success',
        buildCompletedAt: new Date().toISOString(),
      });
    } catch (e) {
      console.warn('[Workbench] resume build-session patch failed', e);
    }
    onPatchMessageByLlmInteractionId?.(llmInteractionId, { buildStatus: 'success' });
    onBuildStreamTurnCompleted?.(llmInteractionId);
    setActiveSnapshotInteractionId(llmInteractionId);
    return 'success';
  };

  const resumeInterruptedBuildImpl = useCallback(
    async (
      llmInteractionId: string,
      options?: { filesApplied?: boolean; userRequested?: boolean },
    ): Promise<ResumeBuildOutcome> => {
      const { projectId: pid, conversationId: cid } = projectConversationRef.current;
      if (!pid || !cid) return 'skipped';
      if (typeof window === 'undefined' || !window.crossOriginIsolated) return 'skipped';
      if (resumeBuildInFlightRef.current) return 'skipped';
      // Once per turn on its own; a founder pressing Continue on the pill may always try again.
      if (!options?.userRequested && resumeBuildAttemptedRef.current.has(llmInteractionId)) {
        return 'skipped';
      }
      if (workbenchStore.isStreaming.get() || workbenchStore.clientToolsBusy.get()) return 'skipped';

      try {
        const server = await prototyperService.getPrototypeSnapshotByInteraction(
          pid,
          cid,
          llmInteractionId,
        );
        if (server.buildVerified === true) {
          // Another tab (or an earlier attempt this session) already finished it.
          onBuildStreamTurnCompleted?.(llmInteractionId);
          return 'skipped';
        }
      } catch (e) {
        // A draft built from the builder's files has no snapshot yet (P6.4d-3).
        if (isBenignMissingSnapshotError(e) && !options?.filesApplied) return 'skipped';
        // Real network error checking status — fall through and try to resume anyway.
      }

      resumeBuildInFlightRef.current = llmInteractionId;
      resumeBuildAttemptedRef.current.add(llmInteractionId);
      const controller = new AbortController();
      resumeBuildAbortRef.current = controller;
      const watchdog = window.setTimeout(() => controller.abort(), RESUME_BUILD_MAX_MS);
      const capturedTurn = turnSessionRef.current.generation;
      const conversationAtStart = cid;

      // The WebContainer is a tab-wide singleton — a resume that survives a conversation
      // switch or a new stream starting would build the *new* conversation's files under the
      // *old* turn's id. Re-checked after every await below.
      const stillOurs = (): boolean =>
        !controller.signal.aborted &&
        projectConversationRef.current.conversationId === conversationAtStart &&
        !isTurnStale(turnSessionRef.current, capturedTurn) &&
        !workbenchStore.isStreaming.get();

      try {
        if (
          !options?.filesApplied &&
          (activeSnapshotInteractionIdRef.current !== llmInteractionId ||
            !activeSnapshotIdRef.current)
        ) {
          // No hint — passing `hasBuildTimeline: true` would mark this unverified build as
          // known-successful and flip buildStatus to 'success' before we've rebuilt anything.
          await restorePrototypeSnapshotForInteraction(llmInteractionId);
        }
        if (!stillOurs()) return 'skipped';

        const resumeCtx = versionTurnContext(llmInteractionId);

        /**
         * No container in this tab, and no need for one: the builder already holds this turn's
         * files, so it builds them and settles the version itself (light-preview plan F1).
         * Verified on a pass, failed on a code error, untouched when the builder itself could
         * not build — the last of which must never draw a paid fix stream.
         */
        if (lightPreviewRuntimeRef.current) {
          if (!resumeCtx) return 'environment_failure';
          const version = await versionTrackerRef.current?.current(resumeCtx);
          if (!version) return 'environment_failure';
          if (!stillOurs()) return 'skipped';
          // Another tab or device may be finishing this same turn (P6.4e).
          const serverLease = await versionTrackerRef.current?.acquire(
            resumeCtx,
            chefProjectIdForTurn(resumeCtx.llmInteractionId),
          );
          if (serverLease === 'held-elsewhere' || !stillOurs()) return 'skipped';

          workbenchStore.buildStatus.set('building');
          let verdict;
          try {
            verdict = await prototyperService.buildPrototypeVersion(
              resumeCtx.projectId,
              resumeCtx.conversationId,
              version.id,
            );
          } catch (err) {
            // The request never got a verdict: nothing is known about the code, so this is an
            // environment failure and the version stays as it was.
            console.warn('[Workbench] resume build on the builder failed', err);
            workbenchStore.buildStatus.set('error');
            return 'environment_failure';
          }
          if (!stillOurs()) return 'skipped';

          if (verdict.environmentFailure) {
            workbenchStore.buildStatus.set('error');
            return 'environment_failure';
          }
          if (!verdict.built) {
            workbenchStore.buildStatus.set('error');
            // The builder already failed the version; reading it back (rather than failing it
            // again) keeps this from spending a second fix attempt on one build.
            const failed = await versionTrackerRef.current?.current(resumeCtx);
            if (!failed) return 'failed';
            if (stillOurs()) {
              handleVersionFailureRef.current(resumeCtx, {
                versionId: failed.id,
                status: verdict.status,
                fixAllowed: verdict.fixAllowed === true,
                fixAttempts: failed.fixAttempts ?? 0,
                fixAttemptsGranted: failed.fixAttemptsGranted ?? 0,
              });
            }
            return 'fix_handled';
          }

          workbenchStore.buildStatus.set('success');
          workbenchStore.hasUnbuiltEdits.set(false);
          return await finishResumedBuild(llmInteractionId);
        }

        const wc = await waitForWebContainerReady(
          () => webcontainerRef.current ?? workbenchStore.webcontainer.get(),
        );
        // The browser could not provide a WebContainer: an environment problem, not a code
        // failure, so callers must never answer it with a paid fix stream.
        if (!wc) {
          if (resumeCtx) {
            void versionTrackerRef.current?.failed(resumeCtx, { kind: 'environment' });
          }
          return 'environment_failure';
        }
        await waitForTemplateReady();
        if (!stillOurs()) return 'skipped';

        if (resumeCtx) {
          // Another tab or device is building this turn: leave it to them (P6.4e).
          const resumeLease = await versionTrackerRef.current?.acquire(
            resumeCtx,
            chefProjectIdForTurn(resumeCtx.llmInteractionId),
          );
          if (resumeLease === 'held-elsewhere' || !stillOurs()) return 'skipped';
        }
        workbenchStore.buildStatus.set('building');
        const result = await executeBuild(wc, undefined, controller.signal);
        if (!stillOurs()) return 'skipped';
        if (result.environment) {
          workbenchStore.buildStatus.set('error');
          if (resumeCtx) {
            void versionTrackerRef.current?.failed(resumeCtx, { kind: 'environment' });
          }
          void recoverPreviewEnvironment('workspace-unresponsive');
          return 'environment_failure';
        }
        workbenchStore.buildStatus.set(result.ok ? 'success' : 'error');
        if (!result.ok) {
          const failure = resumeCtx
            ? ((await versionTrackerRef.current?.failed(resumeCtx, {
                kind: 'code',
                errorSignature: result.errorSig,
                buildLog: result.formattedOutput,
              })) ?? null)
            : null;
          // Versions API unavailable: callers fall back to one automatic fix per turn.
          if (!failure || !resumeCtx) return 'failed';
          if (stillOurs()) handleVersionFailureRef.current(resumeCtx, failure);
          return 'fix_handled';
        }
        return await finishResumedBuild(llmInteractionId);
      } catch (e) {
        if (e instanceof BuildAbortedError || controller.signal.aborted) return 'skipped';
        if (isSupersededRestoreError(e)) return 'skipped';
        console.warn('[Workbench] resumeInterruptedBuild failed', e);
        if (workbenchStore.buildStatus.get() === 'building') {
          workbenchStore.buildStatus.set('error');
        }
        // `executeBuild` reports code errors as `{ ok: false }` (handled above as 'failed');
        // an exception here means restore, template setup or the runtime broke.
        const environmentCtx = versionTurnContext(llmInteractionId);
        if (environmentCtx) {
          void versionTrackerRef.current?.failed(environmentCtx, { kind: 'environment' });
        }
        return 'environment_failure';
      } finally {
        window.clearTimeout(watchdog);
        if (resumeBuildAbortRef.current === controller) {
          resumeBuildAbortRef.current = null;
        }
        resumeBuildInFlightRef.current = null;
      }
    },
    [
      restorePrototypeSnapshotForInteraction,
      runAutoPrototypeSnapshot,
      onBuildStreamTurnCompleted,
      onPatchMessageByLlmInteractionId,
    ],
  );

  // Stable identity — `resumeInterruptedBuildImpl` changes whenever `runAutoPrototypeSnapshot`
  // does (e.g. on every `activeSnapshotInteractionId` change), but it lands in downstream
  // effect dependency arrays that shouldn't re-run just because that identity churned.
  const resumeInterruptedBuildRef = useRef(resumeInterruptedBuildImpl);
  useEffect(() => {
    resumeInterruptedBuildRef.current = resumeInterruptedBuildImpl;
  }, [resumeInterruptedBuildImpl]);
  const resumeInterruptedBuild = useCallback(
    (
      llmInteractionId: string,
      options?: { userRequested?: boolean },
    ): Promise<ResumeBuildOutcome> => resumeInterruptedBuildRef.current(llmInteractionId, options),
    [],
  );

  /**
   * A reopened unfinished draft with no build-start snapshot (the tab closed before it uploaded
   * one), P6.4d-3: put the files the builder saved for this turn into the container and finish the
   * build locally, with no LLM turn. `files_unavailable` when the builder has none for this turn.
   */
  const resumeDraftFromServerFiles = useCallback(
    async (llmInteractionId: string): Promise<ResumeBuildOutcome> => {
      const version = turnVersionsRef.current.get(llmInteractionId);
      const ctx = versionTurnContext(llmInteractionId);
      if (!version || !ctx || version.status !== 'draft') return 'skipped';
      if (resumeBuildInFlightRef.current) return 'skipped';
      if (workbenchStore.isStreaming.get() || workbenchStore.clientToolsBusy.get()) return 'skipped';

      let files: Record<string, string>;
      try {
        ({ files } = await prototyperService.getPrototypeVersionFiles(
          ctx.projectId,
          ctx.conversationId,
          version.id,
        ));
      } catch (e) {
        console.warn('[Workbench] draft files unavailable', e);
        return 'files_unavailable';
      }
      if (Object.keys(files).length === 0) return 'files_unavailable';

      // The light preview takes these files into the store; only a WebContainer has to be
      // waited for.
      if (!lightPreviewRuntimeRef.current) {
        const wc = await waitForWebContainerReady(
          () => webcontainerRef.current ?? workbenchStore.webcontainer.get(),
        );
        if (!wc) return 'environment_failure';
        await waitForTemplateReady();
      }
      if (
        projectConversationRef.current.conversationId !== ctx.conversationId ||
        workbenchStore.isStreaming.get()
      ) {
        return 'skipped';
      }

      try {
        const blob = await buildSnapshotZipFromFiles(files);
        await withRestoreMutex(restoreMutexRef.current, llmInteractionId, async (signal) => {
          await applySnapshotBlobToRuntime(blob, signal);
        });
      } catch (e) {
        if (isSupersededRestoreError(e)) return 'skipped';
        console.warn('[Workbench] applying draft files failed', e);
        return 'environment_failure';
      }
      return resumeInterruptedBuildRef.current(llmInteractionId, { filesApplied: true });
    },
    [],
  );

  /**
   * A reopened turn whose version failed with an attempt left (P6.4c): restore the turn's files
   * and start its fix stream. No local rebuild first: the same error would count as a repeat.
   */
  const resumeFailedVersion = useCallback(
    async (llmInteractionId: string): Promise<ResumeBuildOutcome> => {
      const version = turnVersionsRef.current.get(llmInteractionId);
      const ctx = versionTurnContext(llmInteractionId);
      if (!version || !ctx || version.status !== 'failed' || version.fixAllowed !== true) {
        return 'skipped';
      }
      if (resumeBuildInFlightRef.current) return 'skipped';
      if (workbenchStore.isStreaming.get() || workbenchStore.clientToolsBusy.get()) return 'skipped';
      resumeBuildInFlightRef.current = llmInteractionId;
      try {
        if (
          activeSnapshotInteractionIdRef.current !== llmInteractionId ||
          !activeSnapshotIdRef.current
        ) {
          await restorePrototypeSnapshotForInteraction(llmInteractionId);
        }
        if (
          projectConversationRef.current.conversationId !== ctx.conversationId ||
          workbenchStore.isStreaming.get()
        ) {
          return 'skipped';
        }
        handleVersionFailureRef.current(ctx, {
          versionId: version.id,
          status: version.status,
          fixAllowed: true,
          fixAttempts: version.fixAttempts,
          fixAttemptsGranted: version.fixAttemptsGranted,
        });
        return 'fix_handled';
      } catch (e) {
        if (isSupersededRestoreError(e) || isBenignMissingSnapshotError(e)) return 'skipped';
        console.warn('[Workbench] resume failed version failed', e);
        return 'environment_failure';
      } finally {
        resumeBuildInFlightRef.current = null;
      }
    },
    [restorePrototypeSnapshotForInteraction],
  );

  /** "Try another fix": two more attempts, then the next automatic fix starts (P6.4b). */
  const tryAnotherFix = useCallback(async (): Promise<void> => {
    const attention = versionAttention;
    const ctx = versionTurnContext(attention?.llmInteractionId);
    if (!attention || !ctx) return;
    setVersionAttentionBusy('retry');
    try {
      const granted = await prototyperService.grantPrototypeFixAttempts(
        ctx.projectId,
        ctx.conversationId,
        attention.versionId,
      );
      versionEventsRef.current?.publish({
        conversationId: ctx.conversationId,
        llmInteractionId: attention.llmInteractionId,
        status: granted.status,
      });
      // The fix edits this turn's files: bring them back first if another version is showing.
      if (activeSnapshotInteractionIdRef.current !== attention.llmInteractionId) {
        await restorePrototypeSnapshotForInteraction(attention.llmInteractionId);
      }
      // The founder asked for more: the turn gets a fresh 25 minutes.
      turnBudgetsRef.current.restart(attention.llmInteractionId, Date.now());
      handleVersionFailureRef.current(ctx, {
        versionId: granted.id,
        status: granted.status,
        fixAllowed: true,
        fixAttempts: granted.fixAttempts,
        fixAttemptsGranted: granted.fixAttemptsGranted,
      });
    } catch (e) {
      console.warn('[Workbench] try another fix failed', e);
      setWorkbenchPersistError("Couldn't start another fix. Try again.");
    } finally {
      setVersionAttentionBusy(null);
    }
  }, [versionAttention, restorePrototypeSnapshotForInteraction]);

  /** "Undo this change": set the failed version aside and show the previous verified one. */
  const undoFailedChange = useCallback(async (): Promise<void> => {
    const attention = versionAttention;
    const ctx = versionTurnContext(attention?.llmInteractionId);
    if (!attention || !ctx) return;
    setVersionAttentionBusy('undo');
    try {
      const { currentVersionId } = await prototyperService.discardPrototypeVersion(
        ctx.projectId,
        ctx.conversationId,
        attention.versionId,
      );
      setVersionAttention(null);
      versionEventsRef.current?.publish({
        conversationId: ctx.conversationId,
        llmInteractionId: attention.llmInteractionId,
        status: 'discarded',
      });
      if (currentVersionId) {
        const previous = await prototyperService.getPrototypeVersion(
          ctx.projectId,
          ctx.conversationId,
          currentVersionId,
        );
        if (previous.llmInteractionId) {
          await restorePrototypeSnapshotForInteraction(previous.llmInteractionId);
        }
      }
    } catch (e) {
      console.warn('[Workbench] undo failed change failed', e);
      setWorkbenchPersistError("Couldn't undo this change. Try again.");
    } finally {
      setVersionAttentionBusy(null);
    }
  }, [versionAttention, restorePrototypeSnapshotForInteraction]);

  /** The failing build output for the card's Details. */
  const loadVersionAttentionDetails = useCallback(async (): Promise<string | null> => {
    const ctx = versionTurnContext(versionAttention?.llmInteractionId);
    if (!versionAttention || !ctx) return null;
    const detail = await prototyperService.getPrototypeVersion(
      ctx.projectId,
      ctx.conversationId,
      versionAttention.versionId,
    );
    return detail.buildLog;
  }, [versionAttention]);

  const uploadVerifiedPrototypeSnapshot = useCallback(async (): Promise<string | undefined> => {
    const streamingTurnId =
      streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId ?? null;
    const isLiveWorkingCopy =
      !activeSnapshotInteractionId ||
      !streamingTurnId ||
      activeSnapshotInteractionId === streamingTurnId;

    if (!isLiveWorkingCopy && activeSnapshotId) {
      return activeSnapshotId;
    }

    const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
    if (wc) {
      // Manual editor edits are written to the store immediately but only synced into the
      // WebContainer filesystem on a debounce (`EditorPanel`'s `handleChange`) — without this,
      // a snapshot forced right after a fast edit-then-publish click can be zipped from store
      // content the container never actually had loaded, silently diverging from build.ts's
      // own pre-build flush.
      await flushDirtyEditorFilesBeforeStream(wc).catch((err) => {
        console.warn('[Workbench] flush before publish snapshot failed', err);
      });
    }

    const finalized = await runAutoPrototypeSnapshot('build', { buildVerified: true, force: true });
    const snapshotId = finalized?.snapshotId ?? activeSnapshotId ?? undefined;
    if (!snapshotId) {
      // Nothing to hand the publish call — better to fail loudly here than let the server
      // silently fall back to whatever it considers the conversation's "latest ready"
      // snapshot, which may not be the one currently showing in the preview.
      throw new Error(
        'Could not prepare a build for publish. Try Rebuild, then publish again.',
      );
    }
    return snapshotId;
  }, [
    runAutoPrototypeSnapshot,
    activeSnapshotInteractionId,
    activeSnapshotId,
    streamingLlmInteractionId,
  ]);

  const retrySnapshotBootstrap = useCallback((): void => {
    setSnapshotRestoreError(null);
    setSnapshotRestoreNotice(null);
    // ConversationPage skips the full-panel preparing overlay while this is true, so the
    // workbench (and its banners) stays mounted — a second failure shows the banner again
    // instead of a blank panel.
    setSnapshotRestoreRetrying(true);
    // Refetch the index before choosing what to restore: the entry that failed may have been
    // superseded or deleted. Marking it loading in this same update stops the bootstrap
    // effect from reading the stale index during this commit.
    setSnapshotIndexLoading(true);
    setSnapshotIndexNonce((n) => n + 1);
    setBootstrapRetryNonce((n) => n + 1);
    setSnapshotBootstrap('pending');
  }, []);

  useEffect(() => {
    if (snapshotBootstrap !== 'pending') {
      setSnapshotRestoreRetrying(false);
    }
  }, [snapshotBootstrap]);

  const getSnapshotIndexEntry = useCallback((llmInteractionId: string) => {
    return snapshotIndexRef.current.get(llmInteractionId);
  }, []);

  /**
   * Genuinely abort the outgoing chat's stream before `workbenchChatId` moves on to a new
   * conversation or turn. `useChat`'s `id` change swaps to a brand-new `Chat` instance
   * synchronously during render (`@ai-sdk/react`'s internal `shouldRecreateChat`), and
   * `onToolCall`/`onData` are dispatched through a callbacks ref shared across instances —
   * so without this, an abandoned stream from the previous conversation/turn keeps running
   * and its tool calls/results land on whatever chat is now active. `stop` returned by
   * `useChat` is permanently bound to the instance it came from (an arrow-function class
   * field on `AbstractChat` that captures `this` at construction), so capturing it here and
   * calling it from this effect's cleanup — which fires for the OLD `workbenchChatId` right
   * before the new one's effect runs — reliably cancels the abandoned instance's fetch.
   */
  useEffect(() => {
    const stopOutgoing = stop;
    return () => {
      void stopOutgoing();
    };
  }, [stop]);

  const cancelWorkbenchStream = useCallback((): void => {
    abortCurrentTurn();
    resumeBuildAbortRef.current?.abort();
    resumeBuildAbortRef.current = null;
    clearDeferToolOutputTimeouts();
    cancelStreamContinuationRef.current?.();
    cancelStreamContinuationRef.current = null;
    stop();
    workbenchStore.isStreaming.set(false);
  }, [clearDeferToolOutputTimeouts, stop, abortCurrentTurn]);

  /**
   * Stop button. Closing the build-stream no longer cancels the Chef generation (it finishes
   * the current step and saves the turn), so an explicit Stop also asks the backend to abort
   * it now. Conversation switches keep using `cancelWorkbenchStream` alone.
   */
  const stopAndAbortGeneration = useCallback((): void => {
    const chefProjectId = chefProjectIdRef.current;
    const { projectId: pid, conversationId: cid } = projectConversationRef.current;
    if (chefProjectId && pid && cid) {
      void prototyperService.abortBuildStream(pid, cid, chefProjectId).catch((err) => {
        console.warn('[Workbench] abort request failed', err);
      });
    }
    cancelWorkbenchStream();
  }, [cancelWorkbenchStream]);
  stopAndAbortRef.current = stopAndAbortGeneration;

  const isStreaming = useStore(workbenchStore.isStreaming);
  const previewUrl = useStore(workbenchStore.previewUrl);

  // The per-turn budget is also checked on a timer and when the tab becomes visible again, so
  // one long round without continuations still stops at 25 minutes.
  useEffect(() => {
    if (!isStreaming) return undefined;
    const check = (): void => {
      const lid = streamingBuildLlmInteractionIdRef?.current;
      if (lid && turnBudgetsRef.current.expired(lid, Date.now())) {
        expireTurnBudgetRef.current(lid);
      }
    };
    const intervalId = window.setInterval(check, TURN_BUDGET_CHECK_MS);
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isStreaming, streamingBuildLlmInteractionIdRef]);

  const isLoading = status === 'streaming' || status === 'submitted';

  return useMemo(
    () => ({
      messages,
      toolSteps,
      buildTimeline,
      isLoading,
      isStreaming,
      buildStatus,
      bootStatus,
      previewUrl,
      startGeneration,
      stop: cancelWorkbenchStream,
      stopAndAbort: stopAndAbortGeneration,
      snapshotBootstrap,
      snapshotRestoreError,
      snapshotRestoreNotice,
      snapshotRestoreRetrying,
      restorePrototypeSnapshotForInteraction,
      uploadVerifiedPrototypeSnapshot,
      resumeInterruptedBuild,
      activeSnapshotInteractionId,
      restoreInteractionLoadingId,
      workbenchPersistError,
      snapshotIndexLoading,
      getSnapshotIndexEntry,
      retrySnapshotBootstrap,
      versionAttention,
      versionAttentionCanUndo,
      versionAttentionBusy,
      autoFixLabel,
      tryAnotherFix,
      undoFailedChange,
      loadVersionAttentionDetails,
      turnVersions,
      resumeFailedVersion,
      resumeDraftFromServerFiles,
      repairPreviewCrash,
    }),
    [
      messages,
      toolSteps,
      buildTimeline,
      isLoading,
      isStreaming,
      buildStatus,
      bootStatus,
      previewUrl,
      startGeneration,
      cancelWorkbenchStream,
      stopAndAbortGeneration,
      snapshotBootstrap,
      snapshotRestoreError,
      snapshotRestoreNotice,
      snapshotRestoreRetrying,
      restorePrototypeSnapshotForInteraction,
      uploadVerifiedPrototypeSnapshot,
      resumeInterruptedBuild,
      activeSnapshotInteractionId,
      restoreInteractionLoadingId,
      workbenchPersistError,
      snapshotIndexLoading,
      getSnapshotIndexEntry,
      retrySnapshotBootstrap,
      versionAttention,
      versionAttentionCanUndo,
      versionAttentionBusy,
      autoFixLabel,
      tryAnotherFix,
      undoFailedChange,
      loadVersionAttentionDetails,
      turnVersions,
      resumeFailedVersion,
      resumeDraftFromServerFiles,
      repairPreviewCrash,
    ],
  );
}
